import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Send, Plus, X, FileSignature, Stamp, AlertCircle,
  CheckCircle2, MailQuestion, Trash2, DollarSign, ArrowRight, ArrowLeft,
  Clock, UserCog, History, RotateCcw, Building2, CreditCard, Banknote, Smartphone, MessageCircle,
  Pencil, XOctagon, MapPin,
} from "lucide-react";
import { api, formatApiError, API_BASE } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { fmtNum, fmtDate, fmtDateTime, statusLabel } from "@/lib/format";
import Money from "@/components/Money";
import AppointmentOptionsPanel from "@/components/AppointmentOptionsPanel";
import MuaqqibAssignPanel from "@/components/MuaqqibAssignPanel";

const DOC_TYPE_OPTIONS = [
  { v: "passport", ar: "صورة الجواز", en: "Passport copy" },
  { v: "national_id", ar: "صورة الهوية", en: "National ID" },
  { v: "personal_photo", ar: "صورة شخصية", en: "Personal photo" },
  { v: "marriage_certificate", ar: "عقد الزواج", en: "Marriage certificate" },
  { v: "family_ledger", ar: "كرت العائلة", en: "Family ledger" },
  { v: "bank_statement", ar: "كشف حساب بنكي", en: "Bank statement" },
  { v: "employment_letter", ar: "خطاب من العمل", en: "Employment letter" },
  { v: "other", ar: "مستند آخر", en: "Other document" },
];

const PAYMENT_APPROVE_PERMS = ["payments.approve"];

/* ─────────────────────── i18n Labels (AR / EN) ─────────────────────── */
const LD = {
  ar: {
    section_overview: "نظرة عامة", section_travelers: "المسافرون", section_docs: "المستندات",
    section_payment: "السداد", section_timeline: "السجل", section_notes: "الملاحظات",
    section_appointment: "الموعد",
    client_info: "معلومات العميل", request_details: "تفاصيل الطلب",
    label_name: "الاسم", label_mobile: "الجوال", label_nationality: "الجنسية",
    label_marital: "الحالة الاجتماعية", label_apptype: "نوع التقديم",
    label_country: "الدولة المطلوبة", label_visa_type: "نوع التأشيرة",
    label_travel_date: "تاريخ السفر", label_translation: "الترجمة",
    label_muaqqib: "المعقّب", label_fees: "الرسوم المحددة",
    label_policy: "موافقة السياسة", label_submitted_at: "تاريخ التقديم",
    label_passport: "رقم الجواز", label_dob: "تاريخ الميلاد",
    label_passport_expiry: "تاريخ انتهاء الجواز", label_sex: "الجنس",
    label_id_iqama: "رقم الهوية / الإقامة", label_nat_type: "نوع الجنسية",
    label_email: "البريد الإلكتروني", label_notes: "ملاحظات",
    yes_50: "نعم", persons: "شخص",
    apptype_family: "عائلة", apptype_individual: "فردي",
    no_travelers: "لا يوجد مسافرون مسجّلون.",
    travelers_count: (n) => `${n} مسافر`,
    nat: { saudi: "مواطن سعودي", resident: "مقيم", foreign: "أجنبي", non_saudi: "غير سعودي" },
    sex: { M: "ذكر", F: "أنثى" },
    marital: { single: "أعزب/عزباء", married: "متزوج/ة", divorced: "مطلّق/ة", widowed: "أرمل/ة" },
    rel: { self: "صاحب الطلب", spouse: "زوج/ة", child: "ابن/ابنة", parent: "والد/ة", sibling: "أخ/أخت", other: "آخر" },
    sla_breached: "متأخرة", sla_warning: "اقتربت من الحد", sla_ok: "ضمن الحد",
    back: "رجوع", back_to_list: "رجوع للقائمة",
    office_submission: "تقديم من المكتب",
    archive: "أرشفة", delete_permanent: "حذف نهائي", reset_stage: "إعادة لمرحلة", reset_stas: "إعادة إستاد",
    confirm_delete: (n) => `حذف نهائي للمعاملة ${n}؟ هذا الإجراء لا يُسترجع.`,
    open_receipt: "📄 فتح الإيصال / الفاتورة", open_official_receipt: "📄 فتح الإيصال الرسمي",
    actions_required: "الإجراءات المطلوبة منك",
    btn_send_to_pricing: "إرسال إلى التسعير",
    btn_setup_invoice: "إعداد الفاتورة وإرسالها",
    btn_approve_payment: "اعتماد السداد",
    btn_send_to_authority: "إرسال إلى الجهة الرسمية",
    btn_request_doc: "طلب مستند من العميل",
    btn_reject_payment: "رفض السداد",
    btn_continue_actions: "اكتملت الإجراءات — متابعة",
    btn_record_payment: "تسجيل دفعة داخل المكتب",
    btn_entity_approved: "اعتمدت الجهة الرسمية",
    btn_upload_appointment: "رفع الموعد للعميل",
    btn_office_completed: "اكتملت إجراءات المكتب",
    hint_appointment_tab: "الانتقال للموعد متاح من تبويب «الموعد»",
    appt_visa_center: "مركز التأشيرات",
    appt_city: "المدينة",
    appt_date: "تاريخ الموعد",
    appt_time: "ساعة الموعد (24 ساعة)",
    appt_time_ph: "HH:MM (24h) — مثال 14:30",
    appt_ref: "الرقم المرجعي",
    appt_status: "حالة الموعد",
    appt_status_booked: "محجوز",
    appt_status_rescheduled: "معاد جدولته",
    appt_status_done: "تم",
    appt_status_cancelled: "ملغى",
    appt_save: "حفظ الموعد",
    appt_file_title: "ملف الموعد للعميل (PDF / صورة)",
    appt_file_desc: "ارفع ورقة الموعد أو فاتورة مركز التأشيرات حتى يستطيع العميل تحميلها من بوابته. الملف يُحفظ بشكل دائم.",
    appt_file_size: "كيلوبايت",
    appt_file_preview: "معاينة",
    appt_file_remove: "حذف",
    appt_file_uploading: "جارٍ الرفع…",
    appt_file_upload: "رفع ملف للعميل",
    appt_file_confirm_remove: "حذف الملف المرفوع؟",
    appt_file_available: "متاح للتحميل في بوابة العميل",
    notes_title: "ملاحظات",
    notes_add_placeholder: "أضف ملاحظة…",
    notes_client_visible: "تظهر للعميل في البوابة",
    notes_add: "إضافة",
    notes_empty: "لا توجد ملاحظات.",
    notes_pill_client: "للعميل",
    notes_pill_internal: "داخلية",
  },
  en: {
    section_overview: "Overview", section_travelers: "Travelers", section_docs: "Documents",
    section_payment: "Payment", section_timeline: "Timeline", section_notes: "Notes",
    section_appointment: "Appointment",
    client_info: "Client information", request_details: "Request details",
    label_name: "Full name", label_mobile: "Mobile", label_nationality: "Nationality",
    label_marital: "Marital status", label_apptype: "Submission type",
    label_country: "Destination", label_visa_type: "Visa type",
    label_travel_date: "Travel date", label_translation: "Translation",
    label_muaqqib: "Muaqqib", label_fees: "Fees",
    label_policy: "Policy accepted", label_submitted_at: "Submitted at",
    label_passport: "Passport no.", label_dob: "Date of birth",
    label_passport_expiry: "Passport expiry", label_sex: "Sex",
    label_id_iqama: "National ID / Iqama", label_nat_type: "Nationality type",
    label_email: "Email", label_notes: "Notes",
    yes_50: "Yes", persons: "person(s)",
    apptype_family: "Family", apptype_individual: "Individual",
    no_travelers: "No travelers recorded.",
    travelers_count: (n) => `${n} traveler${n === 1 ? "" : "s"}`,
    nat: { saudi: "Saudi citizen", resident: "Resident", foreign: "Foreign", non_saudi: "Non-Saudi" },
    sex: { M: "Male", F: "Female" },
    marital: { single: "Single", married: "Married", divorced: "Divorced", widowed: "Widowed" },
    rel: { self: "Main applicant", spouse: "Spouse", child: "Child", parent: "Parent", sibling: "Sibling", other: "Other" },
    sla_breached: "Late", sla_warning: "Near SLA", sla_ok: "Within SLA",
    back: "Back", back_to_list: "Back to list",
    office_submission: "Office submission",
    archive: "Archive", delete_permanent: "Delete permanently", reset_stage: "Reset to stage", reset_stas: "STAS reset",
    confirm_delete: (n) => `Permanently delete application ${n}? This cannot be undone.`,
    open_receipt: "📄 Open receipt / invoice", open_official_receipt: "📄 Open official receipt",
    actions_required: "Actions required from you",
    btn_send_to_pricing: "Send to pricing",
    btn_setup_invoice: "Set up & send invoice",
    btn_approve_payment: "Approve payment",
    btn_send_to_authority: "Send to authority",
    btn_request_doc: "Request a document from the client",
    btn_reject_payment: "Reject payment",
    btn_continue_actions: "Done — continue",
    btn_record_payment: "Record in-office payment",
    btn_entity_approved: "Authority approved",
    btn_upload_appointment: "Upload appointment for client",
    btn_office_completed: "Office completed",
    hint_appointment_tab: "Move to appointment is available from the 'Appointment' tab",
    appt_visa_center: "Visa centre",
    appt_city: "City",
    appt_date: "Appointment date",
    appt_time: "Appointment time (24h)",
    appt_time_ph: "HH:MM (24h) — e.g. 14:30",
    appt_ref: "Reference number",
    appt_status: "Appointment status",
    appt_status_booked: "Booked",
    appt_status_rescheduled: "Rescheduled",
    appt_status_done: "Done",
    appt_status_cancelled: "Cancelled",
    appt_save: "Save appointment",
    appt_file_title: "Appointment file for the client (PDF / image)",
    appt_file_desc: "Upload the appointment sheet or visa-centre invoice so the client can download it from their portal. The file is persisted permanently.",
    appt_file_size: "KB",
    appt_file_preview: "Preview",
    appt_file_remove: "Remove",
    appt_file_uploading: "Uploading…",
    appt_file_upload: "Upload file for client",
    appt_file_confirm_remove: "Delete the uploaded file?",
    appt_file_available: "Available for download in the client portal",
    notes_title: "Notes",
    notes_add_placeholder: "Add a note…",
    notes_client_visible: "Show to client in the portal",
    notes_add: "Add",
    notes_empty: "No notes yet.",
    notes_pill_client: "Client-visible",
    notes_pill_internal: "Internal",
  },
};
const ld = (lang) => LD[lang === "en" ? "en" : "ar"];

export default function ApplicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme, dir, lang } = useI18n();
  const T = ld(lang);
  const { user } = useAuth();
  const [app, setApp] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [files, setFiles] = useState([]);
  const [payments, setPayments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [section, setSection] = useState("overview");
  const [sla, setSla] = useState(null);
  const [showReassign, setShowReassign] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [showWhatsApp, setShowWhatsApp] = useState(false);

  const isRoot = !!user?.role?.is_root;
  const roleCode = user?.role?.code;
  const isManager = isRoot || roleCode === "general_manager" || roleCode === "deputy_manager";
  const canApprovePay = isRoot ||
    (roleCode === "general_manager") ||
    (roleCode === "deputy_manager");

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const [a, t, f, p, n, s] = await Promise.all([
        api.get(`/workspace/applications/${id}`),
        api.get(`/workspace/applications/${id}/timeline`),
        api.get(`/workspace/applications/${id}/attachments`),
        api.get(`/workspace/applications/${id}/payments`),
        api.get(`/workspace/applications/${id}/notes`),
        api.get(`/workspace/applications/${id}/sla`).catch(() => ({ data: null })),
      ]);
      setApp(a.data); setTimeline(t.data.items); setFiles(f.data.items);
      setPayments(p.data.items); setNotes(n.data.items);
      setSla(s.data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const css = makeStyles(theme);

  if (loading) return <div dir={dir} style={css.page}><div style={css.loading}>…</div></div>;
  if (err) return (
    <div dir={dir} style={css.page} data-testid="app-detail-error">
      <button onClick={() => navigate("/applications")} style={css.back} data-testid="app-error-back">
        {dir === "rtl" ? "→" : "←"} {T.back_to_list}
      </button>
      <div style={{ ...css.errBox, marginTop: 16 }}>
        <div style={{ fontWeight: 800, marginBottom: 6 }}>{err}</div>
        <div style={{ fontSize: 12, opacity: 0.8 }}>
          قد تكون المعاملة أُسندت لزميل آخر، أو لا تملك صلاحية مشاهدتها. تواصل مع المدير إذا كنت تحتاج الوصول.
        </div>
      </div>
    </div>
  );
  if (!app) return null;

  const onTransition = async (toCode) => {
    if (!window.confirm(`تأكيد الانتقال إلى: ${toCode}؟`)) return;
    try {
      await api.post(`/applications/${id}/transition`, { to_status_code: toCode });
      await load();
    } catch (e) { alert(formatApiError(e)); }
  };

  const sections = [
    { k: "overview", l: T.section_overview },
    { k: "travelers", l: T.section_travelers },
    { k: "docs", l: T.section_docs },
    { k: "payment", l: T.section_payment },
    { k: "timeline", l: T.section_timeline },
    { k: "notes", l: T.section_notes },
    { k: "appointment", l: T.section_appointment },
  ];

  return (
    <div dir={dir} style={css.page} data-testid="app-detail-page">
      {/* Header */}
      <div style={css.head}>
        <button onClick={() => navigate("/applications")} style={css.back} data-testid="app-back">
          {dir === "rtl" ? "→" : "←"} {T.back}
        </button>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={css.appNo} data-testid="app-number">{app.application_number}</div>
            {sla && <SlaBadge sla={sla} theme={theme} lang={lang} />}
            {app.is_office_submission && (
              <span
                data-testid="office-submission-pill"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 5,
                  background: theme.name === "dark" ? "rgba(212,175,55,0.16)" : "rgba(184,144,42,0.16)",
                  color: theme.gold, padding: "4px 12px", borderRadius: 999,
                  fontSize: 11, fontWeight: 900, letterSpacing: 0.3,
                  border: `1px solid ${theme.borderAccent}`,
                }}
              >
                <Building2 size={12} strokeWidth={2.4} /> {T.office_submission}
              </span>
            )}
          </div>
          <div style={css.appClient}>{app.client?.full_name}</div>
          {/* Biometric city — MANDATORY per business rule; render prominently
              at the top of the application header in gold so the team sees it
              the moment they open the case. Trilingual labels. */}
          {app.biometric_city && (
            <div
              data-testid="biometric-city-pill"
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                alignSelf: "flex-start",
                marginTop: 2, padding: "6px 14px", borderRadius: 999,
                background: "linear-gradient(135deg, rgba(244,208,63,0.22), rgba(184,144,42,0.18))",
                border: `1px solid ${theme.gold}`,
                color: theme.gold, fontSize: 12, fontWeight: 900, letterSpacing: 0.3,
              }}
            >
              <MapPin size={13} strokeWidth={2.5} />
              <span>
                {lang === "en"
                  ? `Biometric: ${({riyadh:"Riyadh",jeddah:"Jeddah",dammam:"Dammam"}[app.biometric_city] || app.biometric_city)}`
                  : lang === "ur"
                    ? `بائیومیٹرک: ${({riyadh:"ریاض",jeddah:"جدہ",dammam:"دمام"}[app.biometric_city] || app.biometric_city)}`
                    : `البصمة: ${({riyadh:"الرياض",jeddah:"جدة",dammam:"الدمام"}[app.biometric_city] || app.biometric_city)}`}
              </span>
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button
            onClick={() => setShowWhatsApp(true)}
            style={{ ...css.btnGhost, color: "#16a34a", borderColor: "rgba(22,163,74,0.4)" }}
            data-testid="app-whatsapp-btn"
          >
            <MessageCircle size={13} strokeWidth={2.4} /> {lang === "en" ? "WhatsApp client" : "واتساب العميل"}
          </button>
          {isManager && (
            <button onClick={() => setShowReassign(true)} style={css.btnGhost} data-testid="app-reassign">
              <UserCog size={13} strokeWidth={2.3} /> {lang === "en" ? "Reassign" : "إعادة إسناد"}
            </button>
          )}
          {isRoot && (
            <button onClick={() => setShowReset(true)} style={css.btnGhost} data-testid="app-stage-reset">
              <RotateCcw size={13} strokeWidth={2.3} /> {T.reset_stage}
            </button>
          )}
          {isRoot && (
            <>
              {app.is_archived ? (
                <button onClick={() => api.post(`/workspace/applications/${id}/restore`).then(load)} style={css.btnGhost} data-testid="app-restore">
                  {lang === "en" ? "Restore" : "استعادة"}
                </button>
              ) : (
                <button onClick={() => api.post(`/workspace/applications/${id}/archive`).then(load)} style={css.btnGhost} data-testid="app-archive">
                  {T.archive}
                </button>
              )}
              <button
                onClick={async () => {
                  if (!window.confirm(T.confirm_delete(app.application_number))) return;
                  try {
                    await api.post(`/workspace/applications/bulk-delete`, { ids: [id] });
                    navigate("/applications");
                  } catch (e) {
                    alert(e?.response?.data?.detail || "تعذّر الحذف");
                  }
                }}
                style={{
                  background: "rgba(220,38,38,0.10)", color: "#dc2626",
                  border: "1px solid #dc2626", padding: "8px 14px",
                  borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 800,
                  display: "inline-flex", alignItems: "center", gap: 4,
                }}
                data-testid="app-delete"
              >
                <Trash2 size={13} strokeWidth={2.3} /> {T.delete_permanent}
              </button>
            </>
          )}
        </div>
      </div>

      {showReassign && (
        <ReassignModal appId={id} currentAssignedTo={app.assigned_to} theme={theme}
          onClose={() => setShowReassign(false)}
          onDone={() => { setShowReassign(false); load(); }}
        />
      )}
      {showReset && (
        <StageResetModal appId={id} currentStatus={app.current_status_code} workflowStatuses={app.workflow?.statuses || []} theme={theme}
          onClose={() => setShowReset(false)}
          onDone={() => { setShowReset(false); load(); }}
        />
      )}
      {showWhatsApp && (
        <WhatsAppModal
          app={app} theme={theme} lang={lang} dir={dir}
          onClose={() => setShowWhatsApp(false)}
        />
      )}

      <StatusBar app={app} onTransition={onTransition} css={css} theme={theme} lang={lang} />

      <ActionPanel
        app={app} user={user} canApprovePay={canApprovePay}
        appId={id} onChange={load} theme={theme} lang={lang}
        onJumpToTab={setSection}
      />

      <nav style={css.tabs}>
        {sections.map((s) => (
          <button key={s.k} onClick={() => setSection(s.k)} style={css.tab(section === s.k)} data-testid={`tab-${s.k}`}>
            {s.l}
          </button>
        ))}
      </nav>

      {/* Tab content wrapped in an error boundary — any unexpected error
          inside a section is contained instead of redirecting the user
          back to the applications list. */}
      <SectionErrorBoundary onRetry={() => setSection("overview")}>
      {section === "overview" && <Overview app={app} css={css} theme={theme} T={T} />}
      {/* Muaqqib assignment is critical at the overview level — it has to
          be obvious WHO from the team is handling the file, and which
          travelers the muaqqib covers, before pricing or payment. */}
      {section === "overview" && <MuaqqibAssignPanel application={app} onChanged={load} />}
      {section === "travelers" && <Travelers app={app} css={css} theme={theme} T={T} />}
      {section === "docs" && <Documents files={files} css={css} theme={theme} />}
      {section === "docs" && <DocRequestsPanel appId={id} app={app} onChanged={load} theme={theme} lang={lang} />}
      {section === "payment" && <Payments app={app} payments={payments} canApprove={canApprovePay} onChange={load} css={css} theme={theme} T={T} />}
      {section === "timeline" && <Timeline items={timeline} css={css} theme={theme} lang={lang} />}
      {section === "notes" && <Notes notes={notes} appId={id} onChange={load} css={css} theme={theme} />}
      {section === "appointment" && <Appointment app={app} appId={id} onChange={load} css={css} theme={theme} />}
      {section === "appointment" && (() => {
        // Show the "propose 3 options" panel ONLY before the client picks
        // (i.e. in early stages). Once picked, the appointment fields are
        // auto-filled and the panel is no longer relevant — keeping it
        // around looked like a duplicate, confusing both employees and
        // STAS. The official appointment editor below stays visible always.
        const earlyStages = ["submitted", "awaiting_documents", "under_review",
                              "appointment_selection", "office_intake",
                              "office_appointment_selection"];
        const showOptionsPanel = !app.appointment_chosen && earlyStages.includes(app.current_status_code);
        return (
          <>
            {showOptionsPanel && <AppointmentOptionsPanel application={app} onChanged={load} />}
            {/* STAS / manager override — visible on ANY application that has
                ANY appointment data (locked or not, new or legacy). Lets
                them fix the date/time after the fact. */}
            {isManager && (
              <AppointmentAdminOverride application={app} onChanged={load} />
            )}
            {/* The official appointment card lives outside this file —
                handled by the existing tab content below. */}
          </>
        );
      })()}
      {section === "appointment" && <MuaqqibAssignPanel application={app} onChanged={load} />}
      </SectionErrorBoundary>
    </div>
  );
}

// Tiny class-based error boundary — React requires a class for this.
// Surfaces a friendly message instead of crashing the whole page.
class SectionErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) {
    // Keep the error in the console for debugging but don't blow up the UI.
    // eslint-disable-next-line no-console
    console.error("[SectionErrorBoundary]", err, info);
  }
  render() {
    if (this.state.err) {
      return (
        <div style={{
          padding: 18, margin: 12, borderRadius: 12,
          background: "rgba(220,38,38,0.08)", border: "1px dashed #dc2626",
        }} data-testid="section-error-boundary">
          <div style={{ fontWeight: 900, color: "#dc2626", marginBottom: 6 }}>
            تعذّر عرض هذا القسم.
          </div>
          <div style={{ fontSize: 12, color: "#7f1d1d", marginBottom: 10 }}>
            {String(this.state.err?.message || this.state.err)}
          </div>
          <button
            onClick={() => { this.setState({ err: null }); this.props.onRetry?.(); }}
            style={{
              padding: "8px 16px", borderRadius: 8, border: "1px solid #dc2626",
              background: "transparent", color: "#dc2626", fontWeight: 800, cursor: "pointer",
            }}
            data-testid="section-error-retry"
          >
            رجوع للنظرة العامة
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/* ─────────────────────── Status bar ─────────────────────── */
function StatusBar({ app, onTransition, css, theme, lang }) {
  const T = ld(lang);
  // Show "Open Receipt" button as soon as an invoice exists. The receipt
  // endpoint serves the invoice with the appropriate stamp (مدفوع / بانتظار /
  // مرفوض) based on payment state. Includes both portal AND internal-office
  // workflow statuses so employees see the receipt the moment they collect.
  const hasInvoice = (app.pricing_items_count || 0) > 0
    || !!app.invoice_ref_number
    || ["fees_set", "awaiting_payment", "payment_uploaded",
        "payment_approved", "processing", "in_progress",
        "appointment_booked", "completed",
        // ─── Internal office workflow ───
        "payment_recorded", "receipt_issued", "with_official_entity",
        "official_entity_approved", "appointment_uploaded", "office_completed",
       ].includes(app.current_status_code);
  return (
    <div style={css.statusBar}>
      <div>
        <div style={css.statusLabel}>{lang === "en" ? "Current status" : "الحالة الحالية"}</div>
        <div style={{ ...css.statusVal, color: app.current_status?.color || theme.gold }}>
          {(lang === "en" ? (app.current_status?.name_en || statusLabel(app.current_status_code, "en")) : (app.current_status?.name_ar || statusLabel(app.current_status_code, "ar"))) || app.current_status_code}
        </div>
      </div>
      {hasInvoice && app.application_number && (
        <a
          href={`/portal/track/${app.application_number}/receipt`}
          target="_blank"
          rel="noreferrer"
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "8px 14px", borderRadius: 8,
            background: theme.gold, color: "#0a0a0a", fontWeight: 900,
            fontSize: 12, textDecoration: "none",
          }}
          data-testid="status-open-receipt"
        >
          {T.open_receipt}
        </a>
      )}
    </div>
  );
}

/* ─────────────────────── Action Panel (role + stage aware) ─────────────────────── */
function ActionPanel({ app, user, canApprovePay, appId, onChange, theme, lang, onJumpToTab }) {
  const T = ld(lang);
  const status = app.current_status_code;
  const isEmployee = !user?.role?.is_root && (user?.role?.code === "employee" || user?.permissions?.includes("applications.update"));
  const isManager = canApprovePay;
  const css = panelStyles(theme);

  const [modal, setModal] = useState(null);
  const [empCanPrice, setEmpCanPrice] = useState(false);
  useEffect(() => {
    api.get("/module-visibility")
      .then(({ data }) => setEmpCanPrice(!!data?.employees_can_price))
      .catch(() => { /* keep false */ });
  }, []);

  // Auto-open the pricing modal when navigated with `?openPricing=1`
  // (used by OfficeSubmit so the employee sees the SAME modal Abdulaziz uses).
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("openPricing") === "1") {
      setModal("pricing");
      // Clean the query string without reloading.
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const refreshAfter = async () => { setModal(null); await onChange(); };

  // ─── Portal workflow buttons ───
  // The employee can ask the client for additional documents at ANY
  // early stage — submitted / awaiting_documents / under_review.
  // Previously the button only appeared at awaiting_documents which made
  // the feature invisible on most real applications.
  const showEmployeeReview = (isEmployee || isManager) && [
    "submitted", "awaiting_documents", "under_review",
  ].includes(status);
  // STAS can open pricing actions to employees via /admin/module-visibility.
  // Payment approval stays manager-only — by design — for speed + control.
  const canPriceNow = isManager || (isEmployee && empCanPrice);
  // Internal-office apps: employees can refine the auto-seeded invoice at any
  // stage before they hand the file to the official entity. They get the SAME
  // pricing modal Abdulaziz/Naif use (catalog + custom items + breakdown +
  // non-profit toggle + discount).
  const isInternal = app.workflow_path === "internal" || app.is_office_submission;
  const internalPricingStages = ["office_intake", "payment_recorded", "receipt_issued", "with_official_entity"];
  const showInternalPricing = canPriceNow && isInternal && internalPricingStages.includes(status);
  // Portal apps (Iteration 58 — NEW ORDER):
  //   under_review → appointment_selection → fees_set → awaiting_payment
  // Pricing is now allowed AFTER the client picks an appointment
  // (status = appointment_selection AND appointment_chosen is set) or as
  // a legacy fast-track from earlier stages. The "ask for appointment
  // options" action is the primary path from under_review.
  const hasChosenAppt = !!app.appointment_chosen;
  const portalEarlyPricingStages = ["submitted", "under_review", "awaiting_documents", "sent_to_pricing"];
  const showPortalPricing =
    canPriceNow && !isInternal && (
      portalEarlyPricingStages.includes(status) ||
      (status === "appointment_selection" && hasChosenAppt)
    );
  const showPricing = showPortalPricing || showInternalPricing;
  // "Propose 3 appointments" — appears in early stages, before pricing.
  const showProposeAppointments =
    canPriceNow && !isInternal &&
    ["submitted", "awaiting_documents", "under_review"].includes(status) &&
    !hasChosenAppt;
  const showPaymentApprove = isManager && status === "payment_uploaded";
  const showContinue = (isEmployee || isManager) && ["processing", "in_progress"].includes(status);
  const showAppointment = (isEmployee || isManager) && status === "appointment_booked";
  const showManualComplete = isManager && ["appointment_booked", "processing", "in_progress"].includes(status);
  // "Record office payment" — ONLY for internal-office submissions where
  // the client is physically at the counter. Portal apps go through the
  // online receipt-upload flow instead (the client uploads bank transfer
  // proof from PortalTrack). Showing this button on portal apps was
  // confusing employees and breaking the audit story.
  const showRecordPayment = (isEmployee || isManager) && isInternal && status === "awaiting_payment";

  // ─── Internal-office workflow buttons (Iteration 33) ───
  // The employee owns these end-to-end. No manager approval required.
  // Each transition is a single click that advances the status.
  const canDriveInternal = isInternal && (isEmployee || isManager || user?.role?.is_root);
  const showInternalSendToEntity   = canDriveInternal && status === "receipt_issued";
  const showInternalEntityApproved = canDriveInternal && status === "with_official_entity";
  const showInternalUploadAppt     = canDriveInternal && status === "official_entity_approved";
  const showInternalComplete       = canDriveInternal && status === "appointment_uploaded";

  const nothing = !showEmployeeReview && !showPricing && !showPaymentApprove && !showContinue && !showAppointment && !showManualComplete && !showRecordPayment
    && !showProposeAppointments
    && !showInternalSendToEntity && !showInternalEntityApproved && !showInternalUploadAppt && !showInternalComplete;
  if (nothing) return null;

  const advance = async (toCode, note) => {
    try {
      await api.post(`/applications/${appId}/transition`, { to_status_code: toCode, note: note || null });
      await onChange();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "تعذّر التحديث");
    }
  };

  return (
    <>
      <div style={css.panel} data-testid="action-panel">
        <div style={css.panelHead}>
          <FileSignature size={18} strokeWidth={2.2} style={{ color: theme.gold }} />
          <span style={css.panelTitle}>{T.actions_required}</span>
        </div>
        <div style={css.btnRow}>
          {showEmployeeReview && (
            <button onClick={() => setModal("requestDoc")} style={css.btnGhost} data-testid="action-request-doc">
              <MailQuestion size={15} strokeWidth={2.2} /> {T.btn_request_doc}
            </button>
          )}
          {showProposeAppointments && (
            <button onClick={() => onJumpToTab?.("appointment")} style={css.btnGold} data-testid="action-propose-appointments">
              <Stamp size={15} strokeWidth={2.2} /> اقترح 3 مواعيد للعميل
            </button>
          )}
          {showPricing && (
            <button onClick={() => setModal("pricing")} style={css.btnGold} data-testid="action-pricing">
              <DollarSign size={15} strokeWidth={2.2} /> {T.btn_setup_invoice}
            </button>
          )}
          {showPaymentApprove && (
            <>
              <button onClick={() => setModal("approvePay")} style={css.btnGold} data-testid="action-approve-payment">
                <CheckCircle2 size={15} strokeWidth={2.2} /> {T.btn_approve_payment}
              </button>
              <button onClick={() => setModal("rejectPay")} style={css.btnDanger} data-testid="action-reject-payment">
                <X size={15} strokeWidth={2.4} /> {T.btn_reject_payment}
              </button>
            </>
          )}
          {showContinue && (
            <button onClick={() => setModal("continue")} style={css.btnGold} data-testid="action-continue">
              <ArrowRight size={15} strokeWidth={2.2} /> {T.btn_continue_actions}
            </button>
          )}
          {showAppointment && (
            <span style={css.hint}>
              <Stamp size={14} strokeWidth={2.2} /> {T.hint_appointment_tab}
            </span>
          )}
          {showManualComplete && (
            <button onClick={() => setModal("manualComplete")} style={css.btnGold} data-testid="action-manual-complete">
              <Stamp size={15} strokeWidth={2.4} /> {T.btn_office_completed}
            </button>
          )}
          {showRecordPayment && (
            <button onClick={() => setModal("recordPayment")} style={css.btnGold} data-testid="action-record-payment">
              <Banknote size={15} strokeWidth={2.4} /> {T.btn_record_payment}
            </button>
          )}

          {/* ─── Internal-office workflow actions (Iteration 33) ─── */}
          {showInternalSendToEntity && (
            <button
              onClick={() => advance("with_official_entity", "تم إرسال الملف للجهة الرسمية")}
              style={css.btnGold} data-testid="action-internal-send-entity"
            >
              <Send size={15} strokeWidth={2.2} /> {T.btn_send_to_authority}
            </button>
          )}
          {showInternalEntityApproved && (
            <button
              onClick={() => advance("official_entity_approved", "اعتمدت الجهة الرسمية الطلب")}
              style={css.btnGold} data-testid="action-internal-entity-approved"
            >
              <CheckCircle2 size={15} strokeWidth={2.2} /> {T.btn_entity_approved}
            </button>
          )}
          {showInternalUploadAppt && (
            <button
              onClick={() => advance("appointment_uploaded", "تم رفع الموعد للعميل")}
              style={css.btnGold} data-testid="action-internal-upload-appt"
            >
              <Stamp size={15} strokeWidth={2.2} /> {T.btn_upload_appointment}
            </button>
          )}
          {showInternalComplete && (
            <button
              onClick={() => advance("office_completed", "اكتملت إجراءات المكتب")}
              style={css.btnGold} data-testid="action-internal-complete"
            >
              <Stamp size={15} strokeWidth={2.4} /> {T.btn_office_completed}
            </button>
          )}
        </div>
      </div>

      {modal === "requestDoc" && <RequestDocModal appId={appId} theme={theme} onClose={() => setModal(null)} onDone={refreshAfter} />}
      {modal === "sendToPricing" && <ConfirmModal title="إرسال إلى قسم التسعير" body="هل تأكدت من سلامة المرفقات وأنها كاملة؟ ستنتقل المعاملة إلى عبدالعزيز/نايف/STAS لتسعيرها." theme={theme} onClose={() => setModal(null)} onConfirm={async () => { await api.post(`/workspace/applications/${appId}/send-to-pricing`); refreshAfter(); }} />}
      {modal === "pricing" && <PricingModal appId={appId} theme={theme} lang={lang} onClose={() => setModal(null)} onDone={refreshAfter} />}
      {modal === "approvePay" && <ApprovePayModal app={app} theme={theme} onClose={() => setModal(null)} onDone={refreshAfter} />}
      {modal === "rejectPay" && <RejectPayModal app={app} theme={theme} onClose={() => setModal(null)} onDone={refreshAfter} />}
      {modal === "continue" && <ConfirmModal title="إكمال الإجراءات الأولية" body="بعد الضغط ستنتقل المعاملة لمرحلة الموعد." theme={theme} onClose={() => setModal(null)} onConfirm={async () => { await api.post(`/workspace/applications/${appId}/continue`); refreshAfter(); }} />}
      {modal === "manualComplete" && <ConfirmModal title="إنهاء إجراءات المكتب" body="هل تريد إنهاء إجراءات المكتب الآن؟ سيتم إغلاق المعاملة وإظهار «اكتملت إجراءات المكتب» للعميل. النظام أيضاً يقوم بالإكمال تلقائياً بعد 4 ساعات من موعد العميل الرسمي." theme={theme} onClose={() => setModal(null)} onConfirm={async () => { await api.post(`/workspace/applications/${appId}/complete`); refreshAfter(); }} /> }
      {modal === "recordPayment" && <RecordPaymentModal app={app} appId={appId} theme={theme} onClose={() => setModal(null)} onDone={refreshAfter} />}
    </>
  );
}

/* ───── Modal: request a document from the client ───── */
function RequestDocModal({ appId, theme, onClose, onDone }) {
  const [docType, setDocType] = useState("passport");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const css = panelStyles(theme);

  const submit = async () => {
    if (!body.trim()) { setErr("اكتب رسالة العميل"); return; }
    setBusy(true); setErr("");
    try {
      await api.post(`/workspace/applications/${appId}/notes`, {
        body: body.trim(),
        is_client_visible: true,
        stage_code: "under_review",
        requires_document_type: docType,
      });
      onDone();
    } catch (e) { setErr(e?.response?.data?.detail || "تعذّر الإرسال"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="طلب مستند إضافي من العميل" onClose={onClose} theme={theme}>
      <Field label="نوع المستند المطلوب" theme={theme}>
        <select value={docType} onChange={(e) => setDocType(e.target.value)} style={inputStyle(theme)} data-testid="doc-type-select">
          {DOC_TYPE_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.ar}</option>)}
        </select>
      </Field>
      <Field label="رسالة احترافية للعميل" theme={theme}>
        <textarea
          value={body} onChange={(e) => setBody(e.target.value)}
          rows={4} placeholder="مثال: عزيزي العميل، يرجى التكرم برفع صورة أوضح للجواز…"
          style={{ ...inputStyle(theme), resize: "vertical" }}
          data-testid="doc-note-body"
        />
      </Field>
      {err && <div style={css.err}>{err}</div>}
      <ModalActions onClose={onClose} onConfirm={submit} confirmText="إرسال الطلب" busy={busy} theme={theme} />
    </Modal>
  );
}

/* ───── Modal: pricing builder (catalog + auto items + discount) ───── */

/**
 * OfficeShareCell — Inline editor for the "office share" of a pricing line.
 * Lets the employee type the exact amount that goes to the office (e.g.
 * "this line is 50 SAR total, only 30 goes to the office"). Shows nothing
 * when the line is marked as non-profit. Auto-clamps to [0, amount].
 */
function OfficeShareCell({ item, theme, onSave }) {
  const total = Number(item.amount) || 0;
  const initial = item.office_revenue_amount != null
    ? Number(item.office_revenue_amount)
    : total;
  const [val, setVal] = useState(String(initial));
  // Re-sync when the parent item changes (e.g. after another save)
  useEffect(() => {
    setVal(String(item.office_revenue_amount != null ? item.office_revenue_amount : total));
  }, [item.office_revenue_amount, total]);
  if (!item.is_office_revenue) return null;
  const commit = () => {
    const n = Math.max(0, Math.min(total, Number(val) || 0));
    if (n !== Number(initial)) onSave(n);
    setVal(String(n));
  };
  const isFull = Number(val) === total;
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10,
                   color: "#16a34a", fontWeight: 700, opacity: 0.95 }}
          title="حصة المكتب الفعلية من هذا البند (يدخل في البونص)"
          data-testid={`office-share-cell-${item.id}`}>
      <span>💼 للمكتب:</span>
      <input
        type="number" min="0" max={total} step="1"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        data-testid={`office-share-input-${item.id}`}
        style={{
          width: 56, padding: "2px 4px", borderRadius: 4,
          background: theme.surface, color: theme.text,
          border: `1px solid ${isFull ? "rgba(22,163,74,0.4)" : "rgba(234,88,12,0.6)"}`,
          fontSize: 11, fontWeight: 800, textAlign: "center", fontFamily: "monospace",
          direction: "ltr",
        }}
      />
      <span style={{ color: theme.textMuted, fontWeight: 600 }}>/ {total} ﷼</span>
    </span>
  );
}

export function PricingModal({ appId, theme, lang, onClose, onDone }) {
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [subtotal, setSubtotal] = useState(0);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [discountVisible, setDiscountVisible] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [finalTotal, setFinalTotal] = useState(0);
  const [selectedCode, setSelectedCode] = useState("");
  const [overridePrice, setOverridePrice] = useState("");
  // Office profit (in SAR) for bundled fee lines like "خدمات الجهة وأتعاب
  // المكتب" — the employee types this explicitly. NEVER shown on the client
  // receipt. Only surfaces when the picked catalog item has
  // has_office_share_input=true.
  const [officeProfit, setOfficeProfit] = useState("");
  // Custom (free-form) item: title + price entered by manager — not stored in catalog
  const [customLabel, setCustomLabel] = useState("");
  const [customPrice, setCustomPrice] = useState("");
  const [customIsRevenue, setCustomIsRevenue] = useState(true);
  // Optional descriptive breakdown sub-items for the current line being added.
  // One bullet per line. PURELY DECORATIVE — they do not affect the total.
  const [customBreakdown, setCustomBreakdown] = useState("");
  const [catalogBreakdown, setCatalogBreakdown] = useState("");
  // Inline structured breakdown editor for existing items. Each row is
  // {label, amount, is_office_revenue}. The UI shows a live countdown so the
  // employee can split the parent line into its real sub-costs and clearly
  // see how much remains to allocate.
  const [editingBreakdownId, setEditingBreakdownId] = useState(null);
  const [editingBreakdownRows, setEditingBreakdownRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const css = panelStyles(theme);

  const refresh = async () => {
    const [pricing, cat, appData] = await Promise.all([
      api.get(`/workspace/applications/${appId}/pricing-items`),
      api.get("/pricing-catalog").catch(() => ({ data: { items: [] } })),
      api.get(`/workspace/applications/${appId}`).catch(() => ({ data: {} })),
    ]);
    setItems(pricing.data.items || []);
    setSubtotal(pricing.data.subtotal || 0);
    setDiscountPercent(pricing.data.discount_percent || 0);
    setDiscountAmount(pricing.data.discount_amount || 0);
    setFinalTotal(pricing.data.final_total || 0);
    setCatalog(cat.data.items || []);
    setDiscountVisible(!!appData.data.pricing_discount_visible);
  };

  useEffect(() => { refresh().catch(() => undefined); }, [appId]); // eslint-disable-line react-hooks/exhaustive-deps

  const autoItems = items.filter((i) => i.is_auto);
  const manualItems = items.filter((i) => !i.is_auto);
  const existingCodes = new Set(items.map((i) => i.catalog_code).filter(Boolean));
  const availableCatalog = catalog.filter((c) => !c.auto_from_form_field && !existingCodes.has(c.code));
  const selectedCat = catalog.find((c) => c.code === selectedCode);

  const addItem = async () => {
    setErr("");
    const isCustom = selectedCode === "__custom__";
    // Resolve label + description + defaults
    let label, description_ar = null, description_en = null, catalog_code = null;
    if (isCustom) {
      label = (customLabel || "").trim();
      if (!label) { setErr("اكتب اسم البند"); return; }
    } else {
      if (!selectedCat) { setErr("اختر بنداً من القائمة"); return; }
      label = selectedCat.name_ar;
      description_ar = selectedCat.description_ar || null;
      description_en = selectedCat.description_en || null;
      catalog_code = selectedCat.code;
    }
    // Price
    const amount = Number(overridePrice);
    if (!amount || amount <= 0) { setErr("اكتب المبلغ الإجمالي للبند"); return; }
    // Office profit — ALWAYS captured per line (user's simplified UX requirement).
    // When left blank we default to the item's default is_office_revenue:
    //   • Catalog default is_office_revenue=true  → full amount = profit
    //   • Catalog default is_office_revenue=false → 0 (pass-through)
    //   • Custom items default to full amount = profit
    let officeShare;
    if (officeProfit === "" || officeProfit === null) {
      const defaultIsRev = isCustom ? true : selectedCat.is_office_revenue;
      officeShare = defaultIsRev ? amount : 0;
    } else {
      const n = Number(officeProfit);
      if (!Number.isFinite(n) || n < 0) { setErr("مكسب المكتب رقم غير صالح"); return; }
      if (n > amount) { setErr("مكسب المكتب لا يمكن أن يتجاوز المبلغ الإجمالي"); return; }
      officeShare = Number(n.toFixed(2));
    }
    setBusy(true);
    try {
      await api.post(`/workspace/applications/${appId}/pricing-items`, {
        label,
        amount,
        is_office_revenue: officeShare > 0,
        catalog_code,
        description_ar,
        description_en,
        office_revenue_amount: officeShare,
        breakdown: [],
      });
      setSelectedCode(""); setCustomLabel(""); setOverridePrice(""); setOfficeProfit("");
      await refresh();
    } catch (e) { setErr(e?.response?.data?.detail || "خطأ"); }
    finally { setBusy(false); }
  };

  const removeItem = async (itemId) => {
    try {
      await api.delete(`/workspace/applications/${appId}/pricing-items/${itemId}`);
      await refresh();
    } catch (e) { setErr(e?.response?.data?.detail || "خطأ"); }
  };

  // Iteration 33 — Non-Profit toggle: flip a pricing item between office-revenue and passthrough.
  const toggleNonProfit = async (item) => {
    try {
      await api.patch(`/workspace/applications/${appId}/pricing-items/${item.id}`, {
        is_office_revenue: !item.is_office_revenue,
        note: "تبديل حالة الربحية من واجهة التسعير",
      });
      await refresh();
    } catch (e) { setErr(e?.response?.data?.detail || "تعذّر التبديل"); }
  };

  // Iteration 34 — Direct "office share" amount override. The employee
  // can type explicitly how much of the line's total goes to the office
  // (the rest is passthrough). Backend clamps to [0, amount].
  const setOfficeShare = async (item, newShare) => {
    const total = Number(item.amount) || 0;
    let share = Number(newShare);
    if (!Number.isFinite(share) || share < 0) share = 0;
    if (share > total) share = total;
    try {
      await api.patch(`/workspace/applications/${appId}/pricing-items/${item.id}`, {
        office_revenue_amount: share,
        is_office_revenue: share > 0,
        note: `تعديل حصة المكتب يدوياً → ${share} من ${total}`,
      });
      await refresh();
    } catch (e) { setErr(e?.response?.data?.detail || "تعذّر تعديل الحصة"); }
  };

  // Save the structured breakdown for an existing item. Each row carries
  // its own amount + office-revenue flag so the bonus engine can later treat
  // them independently.
  const saveBreakdown = async (itemId) => {
    const rows = (editingBreakdownRows || [])
      .map((r) => ({
        label: (r.label || "").trim(),
        amount: Number(r.amount) || 0,
        is_office_revenue: !!r.is_office_revenue,
      }))
      .filter((r) => r.label);
    try {
      await api.patch(`/workspace/applications/${appId}/pricing-items/${itemId}`, {
        breakdown: rows,
        note: "تحديث التفاصيل الداخلية للبند",
      });
      setEditingBreakdownId(null);
      setEditingBreakdownRows([]);
      await refresh();
    } catch (e) { setErr(e?.response?.data?.detail || "تعذّر حفظ التفاصيل"); }
  };

  const applyDiscount = async (val, visibleOverride) => {
    const v = Math.max(0, Math.min(100, Number(val) || 0));
    setDiscountPercent(v);
    try {
      await api.put(`/workspace/applications/${appId}/pricing-discount`, {
        discount_percent: v,
        is_visible_to_client: typeof visibleOverride === "boolean" ? visibleOverride : discountVisible,
      });
      await refresh();
    } catch (e) { setErr(e?.response?.data?.detail || "خطأ"); }
  };

  const toggleDiscountVisibility = async () => {
    const next = !discountVisible;
    setDiscountVisible(next);
    await applyDiscount(discountPercent, next);
  };

  const sendInvoice = async () => {
    if (items.length === 0) { setErr("أضف بنداً واحداً على الأقل"); return; }
    if (!window.confirm(`إرسال الفاتورة بإجمالي ${fmtNum(finalTotal)} ر.س للعميل؟`)) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/workspace/applications/${appId}/pricing-send`);
      onDone();
    } catch (e) { setErr(e?.response?.data?.detail || "تعذّر الإرسال"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="إعداد الفاتورة" onClose={onClose} theme={theme} wide>
      {/* Auto-items section */}
      {autoItems.length > 0 && (
        <div style={css.invoiceSection}>
          <div style={css.invoiceSectionTitle}>
            <CheckCircle2 size={13} strokeWidth={2.4} style={{ color: "#16a34a", marginInlineEnd: 6, verticalAlign: "middle" }} />
            البنود التلقائية (من نموذج العميل)
          </div>
          <div style={css.invoiceTable}>
            {autoItems.map((it) => (
              <div key={it.id} style={css.invoiceRow}>
                <span style={{ flex: 1, fontSize: 13, fontWeight: 700 }}>
                  {it.label}
                  <span style={css.autoChip}>تلقائي</span>
                  <span
                    onClick={() => toggleNonProfit(it)}
                    style={it.is_office_revenue ? css.npToggleOffice : css.npToggleNonProfit}
                    title={it.is_office_revenue ? "اضغط لتحويله إلى غير ربحي" : "اضغط لتحويله إلى دخل المكتب"}
                    data-testid={`np-toggle-${it.id}`}
                  >
                    {it.is_office_revenue ? "● دخل المكتب" : "○ غير ربحي"}
                  </span>
                </span>
                <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                  <span style={{ fontWeight: 900, color: theme.gold }}><Money amount={it.amount} size={13} bold /></span>
                  <OfficeShareCell item={it} theme={theme} onSave={(n) => setOfficeShare(it, n)} />
                </span>
                <button onClick={() => removeItem(it.id)} style={css.removeBtn} data-testid={`remove-auto-${it.catalog_code}`}>
                  <Trash2 size={12} strokeWidth={2.3} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── UNIFIED ADD ITEM ROW (per STAS 2026-02-25 simplification) ──
          One dropdown: catalog picks + "أخرى (نص حر)". Selecting "أخرى"
          reveals the free-text label field. Below the row, ALWAYS shows
          a green "مكسب المكتب" input so employees can't forget to record
          how much profit each line generates. */}
      <Field label="إضافة بند" theme={theme}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          <select
            value={selectedCode}
            onChange={(e) => {
              setSelectedCode(e.target.value);
              setOverridePrice("");
              setOfficeProfit("");
              setCustomLabel("");
            }}
            style={{ ...inputStyle(theme), flex: "2 1 220px", minWidth: 200 }}
            data-testid="pricing-item-select"
          >
            <option value="">— اختر بنداً من الكتالوج —</option>
            {availableCatalog.map((c) => (
              <option key={c.code} value={c.code}>{c.name_ar}</option>
            ))}
            <option disabled>─────────────</option>
            <option value="__custom__">✎ أخرى (اكتب اسم البند بنفسك)</option>
          </select>
          {selectedCode === "__custom__" && (
            <input
              placeholder="اسم البند (مثال: تأشيرة عبور)"
              value={customLabel} onChange={(e) => setCustomLabel(e.target.value)}
              style={{ ...inputStyle(theme), flex: "2 1 180px", minWidth: 160 }}
              data-testid="pricing-custom-label"
              autoFocus
            />
          )}
          <input
            type="number" placeholder="المبلغ الإجمالي (ريال)"
            value={overridePrice} onChange={(e) => setOverridePrice(e.target.value)}
            style={{ ...inputStyle(theme), flex: "1 1 130px", maxWidth: 170,
              textAlign: "center", fontWeight: 800 }}
            data-testid="pricing-item-price"
          />
          <button
            onClick={addItem}
            disabled={busy || !selectedCode || (selectedCode === "__custom__" && !customLabel.trim())}
            style={css.btnGold}
            data-testid="pricing-add-item"
          >
            <Plus size={14} strokeWidth={2.3} />
          </button>
        </div>

        {/* Optional legal description shown to the client (catalog items only) */}
        {selectedCat && selectedCat.description_ar && (
          <div style={{
            marginTop: 8, padding: "6px 10px",
            background: "rgba(212,175,55,0.08)", border: `1px dashed ${theme.gold}`,
            borderRadius: 8, fontSize: 11, color: theme.textSoft, lineHeight: 1.6,
          }}>
            <strong style={{ color: theme.gold }}>يظهر للعميل:</strong> {selectedCat.description_ar}
          </div>
        )}

        {/* GREEN OFFICE PROFIT — ALWAYS visible so no employee forgets */}
        {selectedCode && (
          <div
            data-testid="pricing-office-share-block"
            style={{
              marginTop: 10, padding: "10px 12px",
              background: "rgba(22,163,74,0.12)",
              border: "2px solid #16a34a", borderRadius: 10,
              display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
            }}
          >
            <div style={{ fontSize: 22 }} aria-hidden>💰</div>
            <div style={{ flex: "1 1 200px", minWidth: 180 }}>
              <div style={{ fontSize: 13, fontWeight: 900, color: "#166534", lineHeight: 1.4 }}>
                مكسب المكتب من هذا البند
              </div>
              <div style={{ fontSize: 11, color: "#166534", marginTop: 2, lineHeight: 1.5 }}>
                داخلي فقط — لا يظهر للعميل. اتركه فارغاً لاستخدام القيمة الافتراضية.
              </div>
            </div>
            <input
              type="number" min={0} placeholder="مثال: 800"
              value={officeProfit}
              onChange={(e) => setOfficeProfit(e.target.value)}
              style={{
                ...inputStyle(theme),
                width: 130, textAlign: "center",
                fontWeight: 900, fontSize: 15, color: "#166534",
                borderColor: "#16a34a", borderWidth: 2, background: "#fff",
              }}
              data-testid="pricing-office-share-input"
            />
            {overridePrice && officeProfit && Number(officeProfit) >= 0 && Number(officeProfit) <= Number(overridePrice) && (
              <div style={{ fontSize: 11, color: "#166534", fontWeight: 800, textAlign: "center" }}>
                رسوم جهة (تلقائي):<br/>
                <span dir="ltr">{(Number(overridePrice) - Number(officeProfit)).toFixed(2)} ر.س</span>
              </div>
            )}
          </div>
        )}
      </Field>

      {/* Manual items list */}
      {manualItems.length > 0 && (
        <div style={css.invoiceSection}>
          <div style={css.invoiceSectionTitle}>البنود المضافة يدوياً</div>
          <div style={css.invoiceTable}>
            {manualItems.map((it) => (
              <div key={it.id} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={css.invoiceRow}>
                  <span style={{ flex: 1, fontSize: 13, fontWeight: 700 }}>
                    {it.label}
                    <span
                      onClick={() => toggleNonProfit(it)}
                      style={it.is_office_revenue ? css.npToggleOffice : css.npToggleNonProfit}
                      title={it.is_office_revenue ? "اضغط لتحويله إلى غير ربحي" : "اضغط لتحويله إلى دخل المكتب"}
                      data-testid={`np-toggle-${it.id}`}
                    >
                      {it.is_office_revenue ? "● دخل المكتب" : "○ غير ربحي"}
                    </span>
                  </span>
                  <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                    <span style={{ fontWeight: 900, color: theme.gold }}><Money amount={it.amount} size={13} bold /></span>
                    <OfficeShareCell item={it} theme={theme} onSave={(n) => setOfficeShare(it, n)} />
                  </span>
                  <button
                    onClick={() => {
                      if (editingBreakdownId === it.id) {
                        setEditingBreakdownId(null);
                        setEditingBreakdownRows([]);
                      } else {
                        setEditingBreakdownId(it.id);
                        // Normalise: support old string[] format and new struct format.
                        const norm = (it.breakdown || []).map((b) =>
                          typeof b === "string"
                            ? { label: b, amount: 0, is_office_revenue: false }
                            : { label: b.label || "", amount: Number(b.amount) || 0, is_office_revenue: !!b.is_office_revenue }
                        );
                        // Always provide one empty row at the bottom for quick add.
                        setEditingBreakdownRows([...norm, { label: "", amount: "", is_office_revenue: false }]);
                      }
                    }}
                    style={{ ...css.removeBtn, color: "#16a34a", borderColor: "rgba(22,163,74,0.4)" }}
                    title="تفاصيل داخلية للبند (مع عدّاد تنازلي)"
                    data-testid={`breakdown-toggle-${it.id}`}
                  >
                    {(it.breakdown && it.breakdown.length) ? `📝 ${it.breakdown.length}` : "📝 +"}
                  </button>
                  <button onClick={() => removeItem(it.id)} style={css.removeBtn} data-testid={`remove-${it.id}`}>
                    <Trash2 size={12} strokeWidth={2.3} />
                  </button>
                </div>
                {/* Read-only breakdown bullets (when present and not editing) */}
                {editingBreakdownId !== it.id && Array.isArray(it.breakdown) && it.breakdown.length > 0 && (
                  <ul data-testid={`breakdown-list-${it.id}`}
                    style={{ margin: "0 0 6px", paddingInlineStart: 22, listStyle: "disc",
                      color: theme.textMuted, fontSize: 11, lineHeight: 1.7 }}>
                    {it.breakdown.map((b, bi) => {
                      const isObj = typeof b === "object" && b !== null;
                      const label = isObj ? (b.label || "") : String(b);
                      const amt = isObj ? Number(b.amount || 0) : 0;
                      const isOR = isObj ? !!b.is_office_revenue : false;
                      return (
                        <li key={bi}>
                          <span>{label}</span>
                          {amt > 0 && (
                            <span style={{ marginInlineStart: 6, color: theme.gold, fontWeight: 700 }}>
                              — <Money amount={amt} size={11} />
                            </span>
                          )}
                          {isOR && (
                            <span style={{ marginInlineStart: 6, color: "#16a34a", fontSize: 10, fontWeight: 700 }}>
                              ● للمكتب
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
                {/* Structured inline editor with live remaining counter */}
                {editingBreakdownId === it.id && (() => {
                  const parentAmt = Number(it.amount) || 0;
                  const allocated = (editingBreakdownRows || [])
                    .reduce((s, r) => s + (Number(r.amount) || 0), 0);
                  const remaining = parentAmt - allocated;
                  const remColor = remaining < 0 ? "#dc2626" : remaining === 0 ? "#16a34a" : theme.gold;
                  const update = (idx, key, val) => {
                    setEditingBreakdownRows((rows) => {
                      const next = rows.map((r, i) => i === idx ? { ...r, [key]: val } : r);
                      // Auto-append a fresh empty row when the last one starts being filled.
                      const last = next[next.length - 1];
                      if (last && (last.label || last.amount)) {
                        next.push({ label: "", amount: "", is_office_revenue: false });
                      }
                      return next;
                    });
                  };
                  const removeRow = (idx) => {
                    setEditingBreakdownRows((rows) => rows.filter((_, i) => i !== idx));
                  };
                  const autoFillRemaining = (idx) => {
                    // Convenience: snap this row's amount to consume all the remaining.
                    setEditingBreakdownRows((rows) => rows.map((r, i) => {
                      if (i !== idx) return r;
                      const others = rows.reduce((s, rr, j) => j === i ? s : s + (Number(rr.amount) || 0), 0);
                      return { ...r, amount: Math.max(0, parentAmt - others) };
                    }));
                  };
                  return (
                    <div style={{
                      margin: "4px 0 10px", padding: 12, borderRadius: 10,
                      background: theme.bg, border: `1px dashed ${theme.borderSoft}`,
                    }} data-testid={`breakdown-editor-${it.id}`}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                        marginBottom: 10, gap: 8, flexWrap: "wrap" }}>
                        <div style={{ fontSize: 12, fontWeight: 800, color: theme.text }}>
                          تفاصيل البند الداخلية
                        </div>
                        <div style={{
                          fontSize: 12, fontWeight: 900, padding: "5px 10px", borderRadius: 999,
                          background: `${remColor}1a`, color: remColor, border: `1px solid ${remColor}55`,
                        }} data-testid={`breakdown-remaining-${it.id}`}>
                          {remaining >= 0 ? "متبقّي للتوزيع: " : "تجاوز الحد بـ "}
                          <Money amount={Math.abs(remaining)} size={12} bold />
                          {" "}<span style={{ opacity: 0.7, fontWeight: 600 }}>
                            (من <Money amount={parentAmt} size={11} />)
                          </span>
                        </div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {editingBreakdownRows.map((row, idx) => (
                          <div key={idx} style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 110px auto auto",
                            gap: 6, alignItems: "center",
                          }} data-testid={`breakdown-row-${it.id}-${idx}`}>
                            <input
                              value={row.label}
                              onChange={(e) => update(idx, "label", e.target.value)}
                              placeholder="اسم التفصيل (مثال: تأمين سفر)"
                              data-testid={`breakdown-label-${it.id}-${idx}`}
                              style={{ padding: "8px 10px", borderRadius: 6,
                                background: theme.surface, color: theme.text,
                                border: `1px solid ${theme.border}`, fontSize: 12 }}
                            />
                            <input
                              type="number" min="0" step="1"
                              value={row.amount}
                              onChange={(e) => update(idx, "amount", e.target.value)}
                              onDoubleClick={() => autoFillRemaining(idx)}
                              placeholder="المبلغ"
                              title="انقر مرتين لإكمال المبلغ المتبقي تلقائياً"
                              data-testid={`breakdown-amount-${it.id}-${idx}`}
                              style={{ padding: "8px 10px", borderRadius: 6,
                                background: theme.surface, color: theme.text,
                                border: `1px solid ${theme.border}`, fontSize: 12,
                                textAlign: "center", fontFamily: "monospace" }}
                            />
                            <label style={{ display: "flex", alignItems: "center", gap: 4,
                              fontSize: 10, color: theme.textMuted, cursor: "pointer", padding: "0 4px" }}
                              title="حدد إذا كان هذا التفصيل دخل صافي للمكتب">
                              <input
                                type="checkbox"
                                checked={!!row.is_office_revenue}
                                onChange={(e) => update(idx, "is_office_revenue", e.target.checked)}
                                data-testid={`breakdown-or-${it.id}-${idx}`}
                              />
                              للمكتب
                            </label>
                            <button
                              onClick={() => removeRow(idx)}
                              disabled={editingBreakdownRows.length === 1}
                              style={{ ...css.removeBtn, fontSize: 11, padding: "4px 8px",
                                opacity: editingBreakdownRows.length === 1 ? 0.3 : 1 }}
                              data-testid={`breakdown-remove-${it.id}-${idx}`}
                              title="حذف هذا السطر"
                            ><Trash2 size={11} /></button>
                          </div>
                        ))}
                      </div>
                      <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 8, lineHeight: 1.6 }}>
                        💡 المجموع لا يجب أن يساوي مبلغ البند تماماً — هو دليل بصري لتسهيل الحساب.
                        {" "}انقر نقرتين على حقل المبلغ لإكمال المتبقّي تلقائياً.
                      </div>
                      <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "flex-end" }}>
                        <button onClick={() => { setEditingBreakdownId(null); setEditingBreakdownRows([]); }}
                          style={{ ...css.removeBtn, fontSize: 11 }}>إلغاء</button>
                        <button onClick={() => saveBreakdown(it.id)}
                          data-testid={`breakdown-save-${it.id}`}
                          style={{ background: "#16a34a", color: "white", border: "none",
                            padding: "7px 16px", borderRadius: 6, fontSize: 12, fontWeight: 800, cursor: "pointer" }}>
                          حفظ
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
        </div>
      )}

      {items.length === 0 && <div style={css.empty}>لا توجد بنود بعد — أضف من الكتالوج</div>}

      {/* Subtotal + discount + final total */}
      {items.length > 0 && (
        <div style={css.invoiceSummary}>
          <div style={css.invoiceSummaryRow}>
            <span>الإجمالي قبل الخصم</span>
            <Money amount={subtotal} size={13} />
          </div>
          <div style={css.invoiceSummaryRow}>
            <span>نسبة الخصم (%)</span>
            <input
              type="number" min={0} max={100} value={discountPercent}
              onChange={(e) => setDiscountPercent(e.target.value)}
              onBlur={(e) => applyDiscount(e.target.value)}
              style={{ ...inputStyle(theme), width: 90, textAlign: "center", fontFamily: "monospace" }}
              data-testid="pricing-discount-input"
            />
          </div>
          {discountAmount > 0 && (
            <div style={css.invoiceSummaryRow}>
              <span>{lang === "en" ? "Discount amount" : "قيمة الخصم"}</span>
              <span style={{ color: "#dc2626" }}>− <Money amount={discountAmount} size={13} color="#dc2626" /></span>
            </div>
          )}
          <div style={css.invoiceTotal}>
            <span>الإجمالي النهائي</span>
            <span data-testid="invoice-final-total"><Money amount={finalTotal} size={15} bold /></span>
          </div>

          {/* Manager-only control: discount visibility.
              The official invoice is the SOLE reference for what fees include —
              no per-application "fees include external" override exists. */}
          {discountAmount > 0 && (
            <div style={{
              marginTop: 14, padding: 12, borderRadius: 10,
              border: `1px dashed ${theme.borderSoft}`, background: theme.surface,
            }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: theme.text, cursor: "pointer" }} data-testid="discount-visible-toggle">
                <input type="checkbox" checked={discountVisible} onChange={toggleDiscountVisibility} />
                <span>
                  <strong>إظهار الخصم للعميل</strong>
                  <span style={{ color: theme.textMuted, fontSize: 11, marginInlineStart: 6 }}>
                    (لو غير مُفعّل: العميل يشوف السعر النهائي فقط بدون أي ذكر للخصم)
                  </span>
                </span>
              </label>
            </div>
          )}
        </div>
      )}

      {err && <div style={css.err}>{err}</div>}
      <ModalActions onClose={onClose} onConfirm={sendInvoice} confirmText="إرسال الفاتورة للعميل" busy={busy} theme={theme} />
    </Modal>
  );
}

/* ───── Modal: approve payment with bank_reference ───── */
function ApprovePayModal({ app, theme, onClose, onDone }) {
  const [bankRef, setBankRef] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const css = panelStyles(theme);

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      // Find the latest pending payment
      const { data: pays } = await api.get(`/workspace/applications/${app.id}/payments`);
      const pending = (pays.items || []).find((p) => p.status === "pending");
      if (!pending) { setErr("لا يوجد سداد بانتظار الاعتماد"); setBusy(false); return; }
      await api.post(`/payments/${pending.id}/approve`, {
        note: note.trim() || null,
        bank_reference: bankRef.trim() || null,
      });
      onDone();
    } catch (e) { setErr(e?.response?.data?.detail || "خطأ"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="اعتماد السداد" onClose={onClose} theme={theme}>
      <div style={{
        background: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.3)",
        borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 11,
        color: "#16a34a", fontWeight: 600,
      }}>
        💡 الرقم المرجعي اختياري — اعتمد السداد مباشرة بدون أي حقل إن لم يتوفر.
      </div>
      <Field label="الرقم المرجعي للحوالة (اختياري — اتركه فارغاً إن لم يتوفر)" theme={theme}>
        <input value={bankRef} onChange={(e) => setBankRef(e.target.value)}
          placeholder="اختياري — مثل REF-2026-12345"
          style={{ ...inputStyle(theme), color: bankRef ? theme.text : theme.textMuted }}
          dir="ltr" data-testid="bank-reference" />
      </Field>
      <Field label="ملاحظة داخلية (اختياري)" theme={theme}>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} style={{ ...inputStyle(theme), resize: "vertical" }} data-testid="approve-note" />
      </Field>
      {err && <div style={css.err}>{err}</div>}
      <ModalActions onClose={onClose} onConfirm={submit} confirmText="اعتماد الآن" busy={busy} theme={theme} />
    </Modal>
  );
}

function RejectPayModal({ app, theme, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const css = panelStyles(theme);

  const submit = async () => {
    if (!note.trim()) { setErr("اكتب سبب الرفض"); return; }
    setBusy(true); setErr("");
    try {
      const { data: pays } = await api.get(`/workspace/applications/${app.id}/payments`);
      const pending = (pays.items || []).find((p) => p.status === "pending");
      if (!pending) { setErr("لا يوجد سداد بانتظار الاعتماد"); setBusy(false); return; }
      await api.post(`/payments/${pending.id}/reject`, { note: note.trim() });
      onDone();
    } catch (e) { setErr(e?.response?.data?.detail || "خطأ"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="رفض السداد" onClose={onClose} theme={theme}>
      <Field label="سبب الرفض (مطلوب)" theme={theme}>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} style={{ ...inputStyle(theme), resize: "vertical" }} data-testid="reject-note" />
      </Field>
      {err && <div style={css.err}>{err}</div>}
      <ModalActions onClose={onClose} onConfirm={submit} confirmText="رفض" busy={busy} theme={theme} danger />
    </Modal>
  );
}

/* ───── Modal: record an in-office payment (cash / Mada / Visa / transfer) ───── */
function RecordPaymentModal({ app, appId, theme, onClose, onDone }) {
  const fees = Number(app?.fees_amount || 0);
  const [method, setMethod] = useState("mada");
  const [amount, setAmount] = useState(fees > 0 ? String(fees) : "");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const css = panelStyles(theme);

  const methods = [
    { code: "mada",          label: "مدى",         icon: CreditCard,  hint: "بطاقة مدى عبر شبكة POS" },
    { code: "visa_network",  label: "شبكة فيزا",   icon: CreditCard,  hint: "Visa / MasterCard عبر POS" },
    { code: "bank_transfer", label: "تحويل بنكي", icon: Smartphone,   hint: "حوالة على حساب المكتب" },
    { code: "cash",          label: "كاش",          icon: Banknote,    hint: "نقدي مستلَم بإيصال" },
  ];

  const submit = async () => {
    setErr("");
    const amt = Number(amount);
    if (!amt || amt <= 0) { setErr("اكتب مبلغاً صحيحاً"); return; }
    if (fees > 0 && Math.abs(amt - fees) > 0.5 && !window.confirm(
      `المبلغ المُسجَّل (${amt.toLocaleString("en-US")}) لا يطابق رسوم الفاتورة (${fees.toLocaleString("en-US")}). هل أنت متأكد؟`
    )) return;
    setBusy(true);
    try {
      await api.post(`/workspace/applications/${appId}/record-payment`, {
        method,
        amount: amt,
        reference_number: reference.trim() || null,
        notes: notes.trim() || null,
      });
      onDone();
    } catch (e) {
      setErr(e?.response?.data?.detail || "خطأ");
    } finally { setBusy(false); }
  };

  return (
    <Modal title="تسجيل دفعة داخل المكتب" onClose={onClose} theme={theme}>
      <p style={{ fontSize: 12, color: theme.textSoft, marginBottom: 14, lineHeight: 1.7 }}>
        سجّل الدفع الذي تم استلامه من العميل داخل المكتب. النظام سينقل المعاملة مباشرة لمرحلة «قيد التنفيذ» ويحتسب الدخل ضمن لوحة الموظف.
      </p>
      {fees > 0 && (
        <div style={{
          background: theme.name === "dark" ? "rgba(212,175,55,0.10)" : "rgba(184,144,42,0.10)",
          border: `1px solid ${theme.borderAccent}`,
          color: theme.gold, padding: "8px 12px", borderRadius: 8,
          fontSize: 12, fontWeight: 700, marginBottom: 14,
          display: "flex", justifyContent: "space-between",
        }} data-testid="record-payment-fees-hint">
          <span>رسوم الفاتورة:</span>
          <Money amount={fees} size={13} bold />
        </div>
      )}

      <Field label="طريقة الدفع" theme={theme}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8 }}>
          {methods.map((m) => {
            const Icon = m.icon;
            const active = method === m.code;
            return (
              <button
                key={m.code}
                type="button"
                onClick={() => setMethod(m.code)}
                data-testid={`record-payment-method-${m.code}`}
                style={{
                  padding: "12px 10px",
                  background: active
                    ? (theme.name === "dark" ? "rgba(212,175,55,0.16)" : "rgba(184,144,42,0.18)")
                    : "transparent",
                  border: active ? `2px solid ${theme.gold}` : `1px solid ${theme.border}`,
                  borderRadius: 10, color: active ? theme.gold : theme.text,
                  fontWeight: active ? 900 : 700, fontSize: 13,
                  cursor: "pointer", fontFamily: "inherit",
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                  minHeight: 70,
                }}
              >
                <Icon size={18} strokeWidth={active ? 2.6 : 2.2} />
                <span>{m.label}</span>
                <span style={{ fontSize: 10, color: theme.textMuted, fontWeight: 600 }}>{m.hint}</span>
              </button>
            );
          })}
        </div>
      </Field>

      <Field label="المبلغ (ر.س)" theme={theme}>
        <input
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          style={inputStyle(theme)}
          dir="ltr"
          placeholder="0"
          data-testid="record-payment-amount"
        />
      </Field>

      <Field label="رقم العملية / المرجع (اختياري)" theme={theme}>
        <input
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          style={{ ...inputStyle(theme), fontFamily: "monospace" }}
          dir="ltr"
          placeholder={method === "cash" ? "رقم الإيصال — اختياري" : "رقم العملية على آلة الشبكة / الحوالة"}
          data-testid="record-payment-reference"
        />
      </Field>

      <Field label="ملاحظة (اختياري)" theme={theme}>
        <input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          style={inputStyle(theme)}
          placeholder="ملاحظة سريعة …"
          data-testid="record-payment-notes"
        />
      </Field>

      {err && <div style={css.err} data-testid="record-payment-error">{err}</div>}
      <ModalActions onClose={onClose} onConfirm={submit} confirmText="تسجيل وقبول الدفعة" busy={busy} theme={theme} />
    </Modal>
  );
}

/* ───── Simple confirm modal ───── */
function ConfirmModal({ title, body, theme, onClose, onConfirm }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const css = panelStyles(theme);
  const run = async () => {
    setBusy(true); setErr("");
    try { await onConfirm(); } catch (e) { setErr(e?.response?.data?.detail || "خطأ"); setBusy(false); }
  };
  return (
    <Modal title={title} onClose={onClose} theme={theme}>
      <p style={{ fontSize: 13, lineHeight: 1.8, color: theme.textSoft }}>{body}</p>
      {err && <div style={css.err}>{err}</div>}
      <ModalActions onClose={onClose} onConfirm={run} confirmText="تأكيد" busy={busy} theme={theme} />
    </Modal>
  );
}

/* ───── Reusable Modal frame ───── */
function Modal({ title, children, onClose, theme, wide }) {
  return (
    <div style={modalBg} onClick={onClose}>
      <div style={modalCard(theme, wide)} onClick={(e) => e.stopPropagation()} data-testid="action-modal">
        <div style={modalHead(theme)}>
          <span>{title}</span>
          <button onClick={onClose} style={modalClose(theme)} aria-label="Close"><X size={16} strokeWidth={2.2} /></button>
        </div>
        <div style={modalBody}>{children}</div>
      </div>
    </div>
  );
}
function ModalActions({ onClose, onConfirm, confirmText, busy, theme, danger }) {
  return (
    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
      <button onClick={onClose} disabled={busy} style={ghostBtn(theme)} data-testid="modal-cancel">إلغاء</button>
      <button onClick={onConfirm} disabled={busy} style={danger ? dangerBtn() : goldBtn(theme)} data-testid="modal-confirm">{busy ? "…" : confirmText}</button>
    </div>
  );
}
// (Field component is defined later in this file — used by all sections)
const inputStyle = (theme) => ({
  width: "100%", padding: "10px 12px", borderRadius: 8,
  background: theme.inputBg, border: `1px solid ${theme.inputBorder}`,
  color: theme.text, fontSize: 13, fontFamily: "inherit", boxSizing: "border-box",
});
const modalBg = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };
const modalCard = (theme, wide) => ({
  background: theme.surface, border: `1px solid ${theme.borderAccent}`, borderRadius: 14,
  width: "100%", maxWidth: wide ? 560 : 440, maxHeight: "92vh", overflowY: "auto",
  fontFamily: "'Tajawal','Segoe UI',sans-serif", color: theme.text,
});
const modalHead = (theme) => ({
  padding: "14px 18px", borderBottom: `1px solid ${theme.borderSoft}`,
  display: "flex", justifyContent: "space-between", alignItems: "center",
  color: theme.gold, fontWeight: 900, fontSize: 15,
});
const modalClose = (theme) => ({
  background: "transparent", border: "none", color: theme.text, cursor: "pointer",
  padding: 4, display: "inline-flex",
});
const modalBody = { padding: 18 };
const ghostBtn = (theme) => ({
  padding: "9px 16px", background: "transparent", color: theme.text,
  border: `1px solid ${theme.border}`, borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: 13,
});
const goldBtn = (theme) => ({
  padding: "9px 16px", background: theme.goldGradient,
  color: theme.name === "dark" ? "#0a0a0a" : "#fff",
  border: "none", borderRadius: 8, fontWeight: 900, cursor: "pointer", fontSize: 13,
});
const dangerBtn = () => ({
  padding: "9px 16px", background: "#dc2626", color: "#fff",
  border: "none", borderRadius: 8, fontWeight: 900, cursor: "pointer", fontSize: 13,
});

/* ───── SLA badge ───── */
function SlaBadge({ sla, theme, lang }) {
  const T = ld(lang);
  const mins = sla.elapsed_minutes || 0;
  const isAr = lang !== "en";
  const human = mins < 60
    ? (isAr ? `${mins} د` : `${mins}m`)
    : mins < 600
      ? (isAr ? `${Math.floor(mins / 60)} س ${mins % 60} د` : `${Math.floor(mins / 60)}h ${mins % 60}m`)
      : (isAr ? `${Math.floor(mins / 60)} س` : `${Math.floor(mins / 60)}h`);
  const cfg = sla.state === "breached"
    ? { bg: "rgba(220,38,38,0.12)", color: "#dc2626", border: "rgba(220,38,38,0.4)", label: T.sla_breached }
    : sla.state === "warning"
    ? { bg: "rgba(245,158,11,0.12)", color: "#d97706", border: "rgba(245,158,11,0.4)", label: T.sla_warning }
    : { bg: "rgba(22,163,74,0.10)", color: "#16a34a", border: "rgba(22,163,74,0.30)", label: T.sla_ok };
  return (
    <span
      data-testid="sla-badge"
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 800,
        background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
        fontFamily: "inherit",
      }}
    >
      <Clock size={11} strokeWidth={2.6} />
      <span>{human}</span>
      <span style={{ opacity: 0.8 }}>· {cfg.label}</span>
    </span>
  );
}


/* ──────────────────── WhatsApp Client Quick-Message Modal ────────────────────
 * Lets staff type a phone number (NOT auto-saved per STAS instruction) and
 * pick a polite Arabic/English template, then opens https://wa.me/<phone>
 * with the message pre-filled. Office name is read from /branding.
 */
function WhatsAppModal({ app, theme, lang, dir, onClose }) {
  const isAr = lang !== "en";
  // Auto-fill the phone from the application (client profile or form_data).
  // Staff can still edit it if they need to send to a different number.
  const savedPhone =
    app.client?.mobile_number
    || app.client_mobile_number
    || app.form_data?.client_mobile_number
    || "";
  const [rawPhone, setRawPhone] = useState(savedPhone);
  const [template, setTemplate] = useState("track");
  const [customMsg, setCustomMsg] = useState("");
  const [brand, setBrand] = useState({ name_ar: "", name_en: "" });

  useEffect(() => {
    api.get("/branding").then(({ data }) => setBrand(data || {})).catch(() => {});
  }, []);

  // Normalise to E.164 (Saudi).
  const normalisePhone = (raw) => {
    const digits = (raw || "").replace(/\D/g, "");
    if (!digits) return "";
    if (digits.startsWith("966")) return digits;
    if (digits.startsWith("0")) return "966" + digits.slice(1);
    if (digits.startsWith("5") && digits.length === 9) return "966" + digits;
    return digits;
  };
  const phoneIntl = normalisePhone(rawPhone);
  const phoneValid = /^9665\d{8}$/.test(phoneIntl);

  const officeName = isAr
    ? (brand.app_name_ar || brand.name_ar || "وكالة التأشيرات للسفر والسياحة")
    : (brand.app_name_en || brand.name_en || "Visa Travel Agency for Travel & Tourism");
  const trackUrl = `${window.location.origin}/portal/track/${app.application_number}`;
  const receiptUrl = `${window.location.origin}/portal/receipt/${app.application_number}`;
  const clientName = app.client?.full_name || (isAr ? "العميل الكريم" : "Dear client");

  const tplBody = template === "track"
    ? (isAr
        ? `عزيزي ${clientName}،\n\nنرفق لكم رابط متابعة معاملتكم وتحديثات الموعد بشكل مباشر:\n${trackUrl}\n\nمع تحيات ${officeName}`
        : `Dear ${clientName},\n\nPlease use the link below to track your application and receive appointment updates:\n${trackUrl}\n\nBest regards, ${officeName}`)
    : template === "receipt"
    ? (isAr
        ? `عزيزي ${clientName}،\n\nنرفق لكم الإيصال الرسمي لخدمتنا. يمكنكم الاطلاع عليه عبر الرابط:\n${receiptUrl}\n\nمع تحيات ${officeName}`
        : `Dear ${clientName},\n\nPlease find your official service receipt at the link below:\n${receiptUrl}\n\nBest regards, ${officeName}`)
    : customMsg;

  const send = () => {
    if (!phoneValid) return;
    const encoded = encodeURIComponent(tplBody);
    window.open(`https://wa.me/${phoneIntl}?text=${encoded}`, "_blank", "noopener");
    onClose();
  };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 60,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }} data-testid="whatsapp-modal-backdrop">
      <div onClick={(e) => e.stopPropagation()} style={{
        background: theme.surface, color: theme.text, borderRadius: 14,
        width: "100%", maxWidth: 480, padding: 20, border: `1px solid ${theme.border}`,
        boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
      }} dir={dir}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          <MessageCircle size={20} color="#16a34a" />
          <strong style={{ fontSize: 16 }}>
            {isAr ? "إرسال واتساب للعميل" : "WhatsApp client"}
          </strong>
        </div>

        <label style={{ display: "block", fontSize: 11, marginBottom: 4, opacity: 0.75 }}>
          {isAr ? "رقم العميل (يمكن تعديله للإرسال لرقم آخر)" : "Client number (editable to send to a different number)"}
        </label>
        <input
          dir="ltr"
          autoComplete="off"
          placeholder="05XXXXXXXX"
          value={rawPhone}
          onChange={(e) => setRawPhone(e.target.value)}
          data-testid="wa-phone"
          style={{
            width: "100%", padding: "10px 12px", borderRadius: 8,
            background: theme.bg, color: theme.text,
            border: `1.5px solid ${rawPhone && !phoneValid ? "#dc2626" : theme.border}`,
            fontSize: 16, fontWeight: 700, letterSpacing: 1, textAlign: "center",
            marginBottom: 4,
          }}
        />
        {rawPhone && !phoneValid && (
          <div style={{ fontSize: 11, color: "#dc2626", marginBottom: 10 }}>
            {isAr ? "صيغة الرقم غير صحيحة — مثال: 0500525822" : "Invalid format — example: 0500525822"}
          </div>
        )}
        {phoneValid && (
          <div style={{ fontSize: 11, color: "#16a34a", marginBottom: 10 }} dir="ltr">
            +{phoneIntl}
          </div>
        )}

        <div style={{ marginTop: 8, marginBottom: 8, fontSize: 12, fontWeight: 800, opacity: 0.8 }}>
          {isAr ? "اختر القالب" : "Pick template"}
        </div>
        <div style={{ display: "grid", gap: 6, marginBottom: 10 }}>
          {[
            { k: "track",  ar: "🔗 رابط متابعة المعاملة",  en: "🔗 Tracking link" },
            { k: "receipt", ar: "🧾 إيصال الخدمة الرسمي", en: "🧾 Official receipt" },
            { k: "custom", ar: "✏️ رسالة مخصصة",          en: "✏️ Custom message" },
          ].map((t) => (
            <label key={t.k} style={{
              display: "flex", alignItems: "center", gap: 8, padding: "8px 10px",
              borderRadius: 8, cursor: "pointer",
              border: `1.5px solid ${template === t.k ? "#16a34a" : theme.borderSoft}`,
              background: template === t.k ? "rgba(22,163,74,0.10)" : theme.bg,
            }}>
              <input type="radio" name="wa-tpl" checked={template === t.k}
                onChange={() => setTemplate(t.k)}
                data-testid={`wa-tpl-${t.k}`} />
              <span style={{ fontSize: 13, fontWeight: 700 }}>{isAr ? t.ar : t.en}</span>
            </label>
          ))}
        </div>

        {template === "custom" ? (
          <textarea
            value={customMsg}
            onChange={(e) => setCustomMsg(e.target.value)}
            placeholder={isAr ? "اكتب رسالتك هنا…" : "Type your message…"}
            data-testid="wa-custom-msg"
            rows={5}
            style={{
              width: "100%", padding: "10px 12px", borderRadius: 8,
              background: theme.bg, color: theme.text,
              border: `1px solid ${theme.border}`,
              fontSize: 13, lineHeight: 1.6, resize: "vertical",
            }}
          />
        ) : (
          <div style={{
            background: theme.bg, color: theme.text,
            padding: "10px 12px", borderRadius: 8,
            border: `1px solid ${theme.borderSoft}`,
            fontSize: 12, whiteSpace: "pre-wrap", lineHeight: 1.7,
            maxHeight: 160, overflowY: "auto",
          }} data-testid="wa-preview">
            {tplBody}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: "10px 14px", borderRadius: 8,
            background: "transparent", color: theme.text,
            border: `1px solid ${theme.border}`, cursor: "pointer",
            fontWeight: 700,
          }} data-testid="wa-cancel">
            {isAr ? "إلغاء" : "Cancel"}
          </button>
          <button
            disabled={!phoneValid || (template === "custom" && !customMsg.trim())}
            onClick={send}
            data-testid="wa-send"
            style={{
              flex: 2, padding: "10px 14px", borderRadius: 8,
              background: "#16a34a", color: "white",
              border: "none", cursor: phoneValid ? "pointer" : "not-allowed",
              fontWeight: 800, opacity: phoneValid ? 1 : 0.55,
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}
          >
            <MessageCircle size={15} /> {isAr ? "فتح واتساب" : "Open WhatsApp"}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ───── Reassign modal ───── */
function ReassignModal({ appId, currentAssignedTo, theme, onClose, onDone }) {
  const css = panelStyles(theme);
  const [employees, setEmployees] = useState([]);
  const [target, setTarget] = useState(currentAssignedTo || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/users/assignable").then((r) => {
      setEmployees(r.data || []);
    }).catch(() => setEmployees([]));
  }, []);

  const apply = async () => {
    setBusy(true); setErr("");
    try {
      await api.put(`/workspace/applications/${appId}/assign`, { user_id: target || null });
      onDone();
    } catch (e) { setErr(e?.response?.data?.detail || "خطأ"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="إعادة إسناد المعاملة" onClose={onClose} theme={theme}>
      <Field label="اختر الموظف" theme={theme}>
        <select value={target} onChange={(e) => setTarget(e.target.value)} style={inputStyle(theme)} data-testid="reassign-select">
          <option value="">— غير مُسندة (Pool) —</option>
          {employees.map((u) => (
            <option key={u.id} value={u.id}>{u.full_name || u.username}</option>
          ))}
        </select>
      </Field>
      {err && <div style={css.err}>{err}</div>}
      <ModalActions onClose={onClose} onConfirm={apply} confirmText="تأكيد الإسناد" busy={busy} theme={theme} />
    </Modal>
  );
}

/* ───── STAS Stage Reset modal ───── */
function StageResetModal({ appId, currentStatus, workflowStatuses, theme, onClose, onDone }) {
  const css = panelStyles(theme);
  const [target, setTarget] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const apply = async () => {
    if (!target) { setErr("اختر مرحلة هدف"); return; }
    if (reason.trim().length < 3) { setErr("اكتب سبب الإعادة (٣ أحرف على الأقل)"); return; }
    setBusy(true); setErr("");
    try {
      await api.post(`/admin/applications/${appId}/reset-stage`, {
        to_status_code: target, reason: reason.trim(),
      });
      onDone();
    } catch (e) { setErr(e?.response?.data?.detail || "خطأ"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="إعادة المعاملة لمرحلة سابقة (STAS)" onClose={onClose} theme={theme}>
      <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 12, padding: 10, background: theme.surfaceAlt, borderRadius: 8 }}>
        المرحلة الحالية: <strong style={{ color: theme.gold, fontFamily: "monospace" }}>{currentStatus}</strong>
        <div style={{ marginTop: 6, opacity: 0.85 }}>
          ستظهر هذه العملية في الـ Timeline وفي Audit. الموظف المسؤول سيتلقى إشعاراً.
        </div>
      </div>
      <Field label="إلى مرحلة" theme={theme}>
        <select value={target} onChange={(e) => setTarget(e.target.value)} style={inputStyle(theme)} data-testid="reset-target">
          <option value="">— اختر —</option>
          {(workflowStatuses || []).filter((s) => s.code !== currentStatus).map((s) => (
            <option key={s.code} value={s.code}>{s.name_ar || s.code}</option>
          ))}
        </select>
      </Field>
      <Field label="سبب الإعادة (يُحفظ في Audit)" theme={theme}>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} style={{ ...inputStyle(theme), fontFamily: "inherit", resize: "vertical" }} placeholder="مثال: المستندات غير مكتملة، إعادة للموظف" data-testid="reset-reason" />
      </Field>
      {err && <div style={css.err}>{err}</div>}
      <ModalActions onClose={onClose} onConfirm={apply} confirmText="تأكيد الإعادة" busy={busy} theme={theme} />
    </Modal>
  );
}



const panelStyles = (theme) => ({
  panel: {
    background: theme.surfaceCard, border: `1.5px solid ${theme.borderAccent}`,
    borderRadius: 14, padding: 16, marginBottom: 14,
  },
  panelHead: { display: "flex", alignItems: "center", gap: 8, marginBottom: 12 },
  panelTitle: { fontWeight: 900, fontSize: 14, color: theme.gold },
  btnRow: { display: "flex", flexWrap: "wrap", gap: 8 },
  btnGhost: {
    padding: "9px 14px", background: "transparent", color: theme.text,
    border: `1px solid ${theme.borderAccent}`, borderRadius: 9,
    fontWeight: 800, cursor: "pointer", fontSize: 12,
    display: "inline-flex", alignItems: "center", gap: 6,
  },
  btnGold: {
    padding: "10px 16px", background: theme.goldGradient,
    color: theme.name === "dark" ? "#0a0a0a" : "#fff",
    border: "none", borderRadius: 9, fontWeight: 900, cursor: "pointer", fontSize: 12,
    display: "inline-flex", alignItems: "center", gap: 6, letterSpacing: 0.3,
  },
  btnDanger: {
    padding: "10px 16px", background: "rgba(220,38,38,0.10)",
    color: "#dc2626", border: "1px solid #dc2626", borderRadius: 9,
    fontWeight: 900, cursor: "pointer", fontSize: 12,
    display: "inline-flex", alignItems: "center", gap: 6,
  },
  hint: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: theme.textMuted, padding: "8px 12px" },
  templateChip: {
    padding: "6px 12px", borderRadius: 999,
    background: "transparent", border: `1px solid ${theme.borderAccent}`,
    color: theme.gold, cursor: "pointer", fontWeight: 700, fontSize: 11,
  },
  invoiceTable: {
    background: theme.inputBg, border: `1px solid ${theme.borderSoft}`,
    borderRadius: 8, padding: 8, marginBottom: 10, minHeight: 60,
  },
  invoiceSection: { marginBottom: 14 },
  invoiceSectionTitle: {
    fontSize: 11, fontWeight: 800, color: theme.textMuted,
    letterSpacing: 0.4, marginBottom: 6, textTransform: "uppercase",
  },
  autoChip: {
    marginInlineStart: 8, fontSize: 9, fontWeight: 800,
    padding: "2px 8px", borderRadius: 999,
    background: "rgba(22,163,74,0.15)", color: "#16a34a",
  },
  passChip: {
    marginInlineStart: 8, fontSize: 9, fontWeight: 800,
    padding: "2px 8px", borderRadius: 999,
    background: theme.surfaceAlt, color: theme.textMuted,
  },
  // Iteration 33 — clearer "Non-Profit" / "Office Income" toggle chip on each pricing item.
  // Clickable circle the employee can tap to flip the item between revenue / passthrough.
  npToggleNonProfit: {
    display: "inline-flex", alignItems: "center", gap: 4,
    marginInlineStart: 8, fontSize: 10, fontWeight: 900,
    padding: "3px 9px", borderRadius: 999,
    background: "rgba(234,88,12,0.14)",
    color: "#ea580c",
    border: "1.5px solid rgba(234,88,12,0.55)",
    cursor: "pointer", userSelect: "none",
    transition: "all .15s",
  },
  npToggleOffice: {
    display: "inline-flex", alignItems: "center", gap: 4,
    marginInlineStart: 8, fontSize: 10, fontWeight: 900,
    padding: "3px 9px", borderRadius: 999,
    background: "rgba(22,163,74,0.14)",
    color: "#16a34a",
    border: "1.5px solid rgba(22,163,74,0.55)",
    cursor: "pointer", userSelect: "none",
    transition: "all .15s",
  },
  invoiceSummary: {
    background: theme.surfaceAlt, border: `1px dashed ${theme.borderSoft}`,
    borderRadius: 10, padding: 12, marginTop: 8,
  },
  invoiceSummaryRow: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "6px 4px", fontSize: 13, fontWeight: 700,
    borderBottom: `1px dashed ${theme.borderSoft}`,
  },
  invoiceRow: {
    display: "flex", alignItems: "center", gap: 10,
    padding: "10px 8px", borderBottom: `1px dashed ${theme.borderSoft}`,
  },
  removeBtn: {
    background: "transparent", border: `1px solid ${theme.errorBorder}`,
    color: theme.errorText, padding: 5, borderRadius: 6, cursor: "pointer",
    display: "inline-flex",
  },
  invoiceTotal: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "12px 14px", background: theme.goldGradient,
    color: theme.name === "dark" ? "#0a0a0a" : "#fff",
    borderRadius: 10, fontSize: 14, fontWeight: 900, marginBottom: 10,
  },
  empty: { padding: 20, textAlign: "center", color: theme.textMuted, fontSize: 12 },
  err: {
    background: "rgba(220,38,38,0.10)", border: "1px solid #dc2626",
    color: "#dc2626", padding: 10, borderRadius: 8, fontSize: 12, marginBottom: 8,
  },
});

/* ─────────────────────── Overview ─────────────────────── */
function Overview({ app, css, theme, T }) {
  const fmt = (d) => d ? fmtDateTime(d) : "—";
  // Backend returns the applicant data on `members[0]` (the main applicant)
  // and the trip details directly on `app`. There is no nested `app.client`
  // object — the Overview originally assumed there was one, hence the empty
  // header. We now derive from the real shape.
  const mainMember = (app.members || [])[0] || {};
  const clientName = mainMember.full_name || app.client_full_name || "—";
  const clientMobile = mainMember.mobile_number || app.client_mobile_number || app.client?.mobile_number;
  const natKey = mainMember.nationality_type || app.nationality_type || app.client?.nationality_type;
  const maritalKey = mainMember.marital_status || app.marital_status || app.client?.marital_status;
  // Iqama expiry — surfaced for foreigners and for foreign companion workers.
  // Computed flag for the red warning banner.
  const today = new Date(); today.setHours(0,0,0,0);
  const parseDate = (s) => { if (!s) return null; const d = new Date(s); return isNaN(+d) ? null : d; };
  const mainIqamaExpiry = parseDate(mainMember.iqama_expiry || app.iqama_expiry);
  const workerIqamas = (app.companion_workers || []).map((w, i) => ({
    name: w.full_name || `العامل #${i + 1}`,
    expiry: parseDate(w.iqama_expiry),
  }));
  const expiredOrSoon = (d) => {
    if (!d) return null;
    const diff = Math.floor((d - today) / 86400000);
    if (diff < 0) return { state: "expired", days: -diff };
    if (diff <= 30) return { state: "soon", days: diff };
    return { state: "ok", days: diff };
  };
  const mainAlert = expiredOrSoon(mainIqamaExpiry);
  const workerAlerts = workerIqamas
    .map((w) => ({ ...w, alert: expiredOrSoon(w.expiry) }))
    .filter((w) => w.alert && w.alert.state !== "ok");
  // Biometric city — show the human-friendly localized name.
  const bioCityLabel = ({
    riyadh: "الرياض", jeddah: "جدة", dammam: "الدمام",
  })[app.biometric_city] || app.biometric_city || null;
  // Chosen appointment — front-and-center banner.
  const chosen = app.appointment_chosen;
  const chosenAt = app.appointment_chosen_at;
  const apptDate = app.appointment_date;
  const apptTime = app.appointment_time;

  return (
    <div style={css.section}>
      {/* Iqama-expired / about-to-expire alerts — top priority red banner.
          Visible only when the applicant is non-Saudi OR a foreign worker is
          attached, AND the expiry is missing, expired, or within 30 days. */}
      {(mainAlert && mainAlert.state !== "ok") && (
        <div style={{
          marginBottom: 14, padding: 12, borderRadius: 12,
          background: mainAlert.state === "expired" ? "rgba(220,38,38,0.14)" : "rgba(245,158,11,0.14)",
          border: `2px solid ${mainAlert.state === "expired" ? "#dc2626" : "#f59e0b"}`,
          color: mainAlert.state === "expired" ? "#dc2626" : "#b45309",
          fontWeight: 800, fontSize: 13, lineHeight: 1.8,
        }} data-testid="iqama-alert-main">
          {mainAlert.state === "expired"
            ? `⛔ إقامة مقدّم الطلب منتهية منذ ${mainAlert.days} يوم — يرجى تجديدها قبل المتابعة.`
            : `⚠ إقامة مقدّم الطلب ستنتهي خلال ${mainAlert.days} يوم — يفضّل التجديد قبل تاريخ الموعد.`}
        </div>
      )}
      {workerAlerts.map((w, i) => (
        <div key={i} style={{
          marginBottom: 10, padding: 10, borderRadius: 10,
          background: w.alert.state === "expired" ? "rgba(220,38,38,0.10)" : "rgba(245,158,11,0.10)",
          border: `1.5px solid ${w.alert.state === "expired" ? "#dc2626" : "#f59e0b"}`,
          color: w.alert.state === "expired" ? "#dc2626" : "#b45309",
          fontWeight: 700, fontSize: 12, lineHeight: 1.7,
        }} data-testid={`iqama-alert-worker-${i}`}>
          {w.alert.state === "expired"
            ? `إقامة ${w.name} منتهية منذ ${w.alert.days} يوم — يرجى التجديد.`
            : `إقامة ${w.name} تنتهي خلال ${w.alert.days} يوم.`}
        </div>
      ))}

      {chosen && (
        <div style={{
          marginBottom: 16, padding: 14, borderRadius: 12,
          background: "rgba(22,163,74,0.10)", border: "2px solid #16a34a",
          display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        }} data-testid="chosen-appointment-banner">
          <CheckCircle2 size={32} strokeWidth={2.4} style={{ color: "#16a34a", flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#16a34a", marginBottom: 2 }}>
              الموعد الذي اختاره العميل
            </div>
            <div style={{ fontSize: 18, fontWeight: 900 }} dir="ltr">
              {(chosen.datetime || "").slice(0, 16).replace("T", " — ") || "—"}
            </div>
            <div style={{ fontSize: 11, opacity: 0.7, marginTop: 6 }}>
              تم التأكيد: {fmt(chosen.picked_at || chosenAt)}
            </div>
          </div>
        </div>
      )}

      <h3 style={css.h3}>{T.client_info}</h3>
      <Row label={T.label_name} value={clientName} css={css} />
      <Row label={T.label_mobile} value={clientMobile} ltr css={css} />
      <Row label={T.label_nationality} value={(T.nat && T.nat[natKey]) || (natKey === "non_saudi" ? "غير سعودي" : natKey === "saudi" ? "سعودي" : "—")} css={css} />
      <Row label={T.label_marital} value={(T.marital && T.marital[maritalKey]) || "—"} css={css} />
      <Row label={T.label_apptype} value={app.applicant_type === "family" ? T.apptype_family : T.apptype_individual} css={css} />
      {/* Iqama expiry row — only render for non-Saudi applicants. */}
      {(natKey === "non_saudi" || mainIqamaExpiry) && (
        <Row label="انتهاء الإقامة"
          value={mainIqamaExpiry
            ? <span style={{ color: mainAlert?.state === "expired" ? "#dc2626" : mainAlert?.state === "soon" ? "#b45309" : "inherit", fontWeight: 800 }}>{mainIqamaExpiry.toISOString().slice(0,10)}</span>
            : <span style={{ color: "#dc2626", fontWeight: 800 }}>غير محدد — يجب على العميل إدخاله</span>}
          ltr css={css} />
      )}

      {/* Sponsor / employer — kafil/jihat al-amal. Optional but commonly used
          by embassies as proof of stable income. */}
      {app.sponsor_name && (
        <Row
          label={app.sponsor_type === "company" ? "جهة العمل" : "الكفيل"}
          value={
            <span style={{ fontWeight: 700 }}>
              {app.sponsor_name}
              {app.sponsor_type && (
                <span style={{ fontSize: 10, opacity: 0.65, marginInlineStart: 8, fontWeight: 600 }}>
                  ({app.sponsor_type === "company" ? "شركة" : "شخص"})
                </span>
              )}
            </span>
          }
          css={css}
        />
      )}
      {/* National Address (residential proof) */}
      {app.national_address && (
        <Row
          label="العنوان الوطني"
          value={<span style={{ fontWeight: 600 }}>{app.national_address}</span>}
          css={css}
        />
      )}

      <h3 style={{ ...css.h3, marginTop: 18 }}>{T.request_details}</h3>
      <Row label={T.label_country} value={app.country} css={css} />
      <Row label={T.label_visa_type} value={app.visa_type} css={css} />
      <Row label={T.label_travel_date} value={app.travel_date} css={css} />
      {app.return_date && <Row label="تاريخ العودة" value={app.return_date} css={css} />}
      {/* Biometric city — gold-tinted prominent row. */}
      {bioCityLabel && (
        <div style={{ ...css.row, background: "rgba(212,175,55,0.08)", borderRadius: 8, padding: "8px 12px", margin: "4px 0" }} data-testid="overview-biometric-city">
          <span style={css.rowLabel}>
            <MapPin size={12} strokeWidth={2.4} style={{ verticalAlign: "-2px", marginInlineEnd: 4, color: theme.gold }} />
            مدينة البصمة
          </span>
          <span style={{ ...css.rowValue, fontWeight: 900, color: theme.gold }}>{bioCityLabel}</span>
        </div>
      )}
      {/* Appointment date & time — surfaced even without `chosen` (office-set) */}
      {(apptDate || apptTime) && (
        <Row label="الموعد الرسمي"
          value={<span dir="ltr">{apptDate || "—"}{apptTime ? ` — ${apptTime}` : ""}</span>}
          css={css} />
      )}
      <Row label={T.label_translation} value={app.translation_service ? <span>{T.yes_50} (<Money amount={50} size={12} />)</span> : "—"} css={css} />
      <Row label={T.label_muaqqib} value={app.muaqqib_service ? (() => {
        const passportCount = app.applicant_type === "family"
          ? Math.max((app.members || []).length, 1)
          : 1;
        return <span>{passportCount} {T.persons} × <Money amount={100} size={12} /> = <Money amount={passportCount * 100} size={12} bold /></span>;
      })() : "—"} css={css} />
      <Row label={T.label_fees} value={app.fees_amount ? <Money amount={app.fees_amount} size={13} bold /> : "—"} css={css} />
      <Row label={T.label_policy} value={app.policy_accepted_at ? `${fmt(app.policy_accepted_at)} • IP: ${app.policy_ip || "—"}` : "—"} css={css} />
      <Row label={T.label_submitted_at} value={fmt(app.submission_date || app.created_at)} css={css} />
    </div>
  );
}

function Row({ label, value, ltr, css }) {
  return (
    <div style={css.row}>
      <span style={css.rowLabel}>{label}</span>
      <span style={{ ...css.rowValue, ...(ltr ? { direction: "ltr" } : {}) }}>{value || "—"}</span>
    </div>
  );
}

/* ─────────────────────── Travelers ─────────────────────── */
function Travelers({ app, css, T }) {
  const members = app.members || [];
  if (!members.length) return <div style={css.section}>{T.no_travelers}</div>;
  return (
    <div style={css.section}>
      <h3 style={css.h3}>{T.travelers_count(members.length)}</h3>
      {members.map((m, i) => (
        <div key={m.id || i} style={css.memberCard} data-testid={`member-${i}`}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10, gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 800, fontSize: 14 }}>
              #{i + 1} — {m.full_name || "—"}
              {m.is_companion && (
                <span style={{
                  marginInlineStart: 8, padding: "2px 8px", borderRadius: 999,
                  background: "rgba(212,175,55,0.18)", color: "#d4af37",
                  fontSize: 10, fontWeight: 900, letterSpacing: 0.3,
                }} data-testid={`member-companion-badge-${i}`}>
                  عمالة منزلية{m.job_title ? ` — ${m.job_title}` : ""}
                </span>
              )}
            </span>
            <span style={{ fontSize: 11, color: "#888" }}>{(T.rel && T.rel[m.relationship_to_main]) || m.relationship_to_main || ""}</span>
          </div>
          {m.full_name_ar && <Row label="الاسم بالعربية / Arabic name" value={m.full_name_ar} css={css} />}
          {m.full_name_en && <Row label="الاسم بالإنجليزية / English name" value={m.full_name_en} ltr css={css} />}
          <Row label={T.label_passport} value={m.passport_number} ltr css={css} />
          <Row label={T.label_nationality} value={m.nationality} css={css} />
          <Row label={T.label_dob} value={m.date_of_birth ? fmtDate(m.date_of_birth) : null} ltr css={css} />
          <Row label={T.label_passport_expiry} value={m.expiry_date ? fmtDate(m.expiry_date) : null} ltr css={css} />
          <Row label={T.label_sex} value={(T.sex && T.sex[m.sex]) || m.sex} css={css} />
          <Row label={T.label_id_iqama} value={m.national_id || m.iqama_number} ltr css={css} />
          {m.is_companion && m.job_title && (
            <Row label="الوظيفة" value={m.job_title} css={css} />
          )}
          <Row label={T.label_nat_type} value={(T.nat && T.nat[m.nationality_type]) || "—"} css={css} />
          <Row label={T.label_marital} value={(T.marital && T.marital[m.marital_status]) || "—"} css={css} />
          <Row label={T.label_mobile} value={m.mobile_number} ltr css={css} />
          {m.email && <Row label={T.label_email} value={m.email} ltr css={css} />}
          {m.notes && <Row label={T.label_notes} value={m.notes} css={css} />}
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────── Documents ─────────────────────── */
function Documents({ files, css, theme }) {
  if (!files.length) return <div style={css.section}>لا توجد مستندات.</div>;
  const url = (f) => f.file_url?.startsWith("http") ? f.file_url : `${process.env.REACT_APP_BACKEND_URL}${f.file_url}`;
  return (
    <div style={css.section}>
      <h3 style={css.h3}>{files.length} مرفق</h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 10 }}>
        {files.map((f) => {
          const isImg = (f.content_type || "").startsWith("image/");
          const docLabel = labelDocType(f.file_type);
          const ownerName = f.owner_name || "";
          const ownerJob = f.owner_job_title || "";
          const isCompanion = !!f.owner_is_companion;
          const ownerLine = ownerName
            ? (isCompanion
                ? `${ownerName}${ownerJob ? ` — ${ownerJob}` : " — عمالة منزلية"}`
                : ownerName)
            : "—";
          return (
            <a key={f.id} href={url(f)} target="_blank" rel="noreferrer"
               style={css.docCard} data-testid={`doc-${f.id}`}>
              {isImg ? (
                <img src={url(f)} alt={f.file_name} loading="lazy"
                  style={css.docThumb}
                  onError={(e) => {
                    // When the file is missing on disk (e.g. after a
                    // deploy that lost the volume), swap the broken img
                    // for a friendly placeholder instead of leaving the
                    // browser's broken-image icon.
                    e.currentTarget.style.display = "none";
                    const ph = document.createElement("div");
                    ph.style.cssText = "padding:24px;text-align:center;color:#dc2626;font-size:11px;font-weight:800;background:rgba(220,38,38,0.05);border:1px dashed #dc2626;border-radius:8px;";
                    ph.innerText = "تعذّر تحميل الصورة";
                    e.currentTarget.parentNode.insertBefore(ph, e.currentTarget);
                  }} />
              ) : (
                <div style={css.docPdf}>PDF</div>
              )}
              <div style={{ padding: 10 }}>
                {/* Owner name — the most important label, shown in gold */}
                <div style={{
                  fontSize: 12, fontWeight: 900, color: "#d4af37",
                  marginBottom: 4, whiteSpace: "nowrap", overflow: "hidden",
                  textOverflow: "ellipsis",
                }} data-testid={`doc-owner-${f.id}`}>
                  {ownerLine}
                </div>
                {/* Document type in Arabic */}
                <div style={css.docName} data-testid={`doc-type-${f.id}`}>{docLabel}</div>
                <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 4 }}>
                  {(f.size_bytes / 1024).toFixed(0)} KB
                </div>
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
}
const DOC_LABEL = {
  passport: "الجواز",
  passport_scan: "الجواز",
  national_id: "الهوية الوطنية",
  national_id_scan: "الهوية الوطنية",
  residence: "الإقامة",
  residence_scan: "الإقامة",
  iqama: "الإقامة",
  bank: "كشف حساب 3 أشهر",
  bank6: "كشف حساب 6 أشهر",
  photo: "صورة شخصية",
  old_visas: "تأشيرات سابقة",
  family_card: "بطاقة العائلة",
  marriage: "عقد الزواج",
  salary: "تعريف راتب",
  exit_return: "تأشيرة خروج وعودة",
  receipt: "إيصال السداد",
  companion_passport_scan: "جواز المرافق",
  companion_iqama_scan: "إقامة المرافق",
  passport_absher_qr: "جواز — رمز أبشر",
  saudi_id_qr: "الهوية — رمز التحقق",
};
const labelDocType = (k) => DOC_LABEL[k] || k;

/* ─────────────────────── Payments ─────────────────────── */
/* ─────────────────────── Read-only Pricing Items View (visible to all roles) ─────────────────────── */
function PricingItemsView({ appId, hasItems, theme }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!hasItems) { setData(null); return; }
    api.get(`/workspace/applications/${appId}/pricing-items`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch((e) => { if (!cancelled) setErr(formatApiError(e)); });
    return () => { cancelled = true; };
  }, [appId, hasItems]);

  if (!hasItems) return null;
  if (err) return (
    <div style={{
      padding: 12, marginBottom: 12, borderRadius: 10,
      background: theme.surface, border: `1px solid ${theme.borderSoft}`,
      fontSize: 12, color: theme.textMuted,
    }} data-testid="pricing-view-error">
      تعذّر تحميل بنود التسعير: {err}
    </div>
  );
  if (!data) return (
    <div style={{ padding: 12, marginBottom: 12, color: theme.textMuted, fontSize: 12 }}>
      … جارٍ تحميل بنود التسعير
    </div>
  );

  const items = data.items || [];
  if (!items.length) return null;

  return (
    <div style={{
      padding: 14, marginBottom: 14, borderRadius: 12,
      background: theme.surface, border: `1.5px solid ${theme.borderAccent}`,
    }} data-testid="pricing-items-view">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ fontWeight: 900, fontSize: 14, color: theme.gold }}>
          بنود التسعير ({items.length})
        </div>
        {data.sent_at && (
          <span style={{
            fontSize: 10, fontWeight: 800, padding: "3px 8px", borderRadius: 999,
            background: "rgba(22,163,74,0.14)", color: "#16a34a",
          }}>تم إرسالها للعميل</span>
        )}
      </div>

      <div style={{ display: "grid", gap: 6 }}>
        {items.map((it) => (
          <div key={it.id} data-testid={`pricing-row-${it.id}`} style={{
            display: "grid", gridTemplateColumns: "1fr auto auto", gap: 8, alignItems: "center",
            padding: "8px 10px", borderRadius: 8,
            background: theme.bg, border: `1px solid ${theme.borderSoft}`,
          }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: theme.text }}>{it.label || it.name_ar || "—"}</span>
              <span style={{
                fontSize: 10, fontWeight: 800, alignSelf: "flex-start",
                padding: "2px 8px", borderRadius: 999,
                background: it.is_office_revenue ? "rgba(22,163,74,0.12)" : "rgba(245,158,11,0.14)",
                color: it.is_office_revenue ? "#16a34a" : "#d97706",
                border: `1px solid ${it.is_office_revenue ? "rgba(22,163,74,0.35)" : "rgba(245,158,11,0.35)"}`,
              }}>
                {it.is_office_revenue ? "● دخل المكتب" : "○ غير ربحي"}
              </span>
              {/* Descriptive breakdown sub-items (decorative — do NOT affect the total). */}
              {Array.isArray(it.breakdown) && it.breakdown.length > 0 && (
                <ul data-testid={`pricing-breakdown-${it.id}`}
                  style={{ margin: "4px 0 0", paddingInlineStart: 18, listStyle: "disc",
                    color: theme.textMuted, fontSize: 11, lineHeight: 1.7 }}>
                  {it.breakdown.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              )}
            </div>
            <span style={{ fontSize: 11, color: theme.textMuted, whiteSpace: "nowrap" }}>
              {it.qty && it.qty > 1 ? `${it.qty} × ` : ""}
              {it.unit_price != null && <Money amount={it.unit_price} size={11} />}
            </span>
            <Money amount={it.amount || ((it.unit_price || 0) * (it.qty || 1))} size={13} bold />
          </div>
        ))}
      </div>

      {/* Totals */}
      <div style={{
        marginTop: 12, paddingTop: 10, borderTop: `1px dashed ${theme.borderSoft}`,
        display: "grid", gap: 4, fontSize: 12,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span style={{ color: theme.textMuted }}>الإجمالي قبل الخصم</span>
          <Money amount={data.subtotal} size={12} />
        </div>
        {(data.discount_amount || 0) > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between", color: "#d97706" }}>
            <span>الخصم ({data.discount_percent}%)</span>
            <span>− <Money amount={data.discount_amount} size={12} /></span>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4,
          paddingTop: 6, borderTop: `1px solid ${theme.borderSoft}` }}>
          <span style={{ fontWeight: 900, color: theme.gold }}>الإجمالي النهائي</span>
          <Money amount={data.final_total ?? data.subtotal} size={15} bold />
        </div>
      </div>
    </div>
  );
}

function Payments({ app, payments, canApprove, onChange, css, theme, T }) {
  const { user } = useAuth();
  const isRoot = !!user?.role?.is_root;
  const roleCode = user?.role?.code || "";
  const isManager = isRoot || ["general_manager", "deputy_manager"].includes(roleCode);
  // Stages where the employee can still edit (office hasn't sent the
  // bill to the client yet). Once we leave this set, only managers/STAS
  // may change the invoice.
  const preSendStages = ["submitted", "under_review", "awaiting_documents",
                         "sent_to_pricing", "fees_set", "appointment_selection"];
  const inPreSend = preSendStages.includes(app.current_status_code || "");
  // Final flag: managers always, employees only in pre-send.
  const canEditInvoice = isManager || (roleCode === "employee" && inPreSend);
  // Refund is now available to managers too (was STAS-only).
  const canRefund = isManager;
  const [busy, setBusy] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const review = async (id, action) => {
    const note = window.prompt(`ملاحظة على ${action === "approve" ? "الاعتماد" : "الرفض"} (اختياري):`) || "";
    setBusy(true);
    try {
      await api.post(`/payments/${id}/${action}`, { note });
      await onChange();
    } catch (e) { alert(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const url = (u) => u?.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`;
  const fmt = (d) => d ? fmtDateTime(d) : "—";

  // STAS-only invoice corrections. Live next to the invoice so STAS finds
  // them without leaving the application page. Two actions:
  //   • تعديل الفاتورة → opens the existing /admin/invoice-verify modal in
  //     this page; backend keeps V1 in invoice_revisions and writes V2.
  //   • استرداد + أرشفة → blanks the application: ledger reversed, earnings
  //     trimmed, archived=true, client portal locked.
  const appNum = app.application_number;
  const hasInvoice = !!app.invoice_ref_number || (app.pricing_items_count || 0) > 0;
  const isTerminated = ["cancelled", "refunded"].includes(app.invoice_status || "");
  const reviseInvoice = () => {
    if (!hasInvoice) { alert("الفاتورة غير صادرة بعد"); return; }
    if ((app.pricing_items || []).length === 0) { alert("لا توجد بنود — افتح التسعير أولاً"); return; }
    setEditorOpen(true);
  };
  const [refundModal, setRefundModal] = useState(null);   // { paid, mode, reason, amount }

  const openRefundModal = () => {
    if (!hasInvoice) { alert("الفاتورة غير صادرة بعد"); return; }
    const paid = Number(app?.payment?.amount || 0);
    if (!(paid > 0) || app?.payment?.status !== "approved") {
      alert(
        "لا يمكن الاسترداد لعدم وجود مبلغ مدفوع فعلياً على هذه الفاتورة.\n" +
        "استخدمي إلغاء الفاتورة بدلاً من الاسترداد."
      );
      return;
    }
    setRefundModal({ paid, mode: null, reason: "", amount: String(paid) });
  };

  const confirmRefund = async () => {
    if (!refundModal) return;
    const { mode, reason, amount, paid } = refundModal;
    if (!mode) { alert("اختاري احتساب 10% أو إعفاء العميل أولاً."); return; }
    if (!reason || reason.trim().length < 10) { alert("السبب مطلوب (10 أحرف على الأقل)"); return; }
    const amt = parseFloat(amount) || 0;
    if (amt <= 0 || amt > paid) { alert(`المبلغ يجب أن يكون بين 0 و ${fmtNum(paid)} (المدفوع فعلياً)`); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/invoices/${appNum}/refund`, {
        reason: reason.trim(), amount: amt, exempt_office_fee: mode === "exempt",
      });
      const ret = data?.office_retention ?? 0;
      const net = data?.net_refund_to_client ?? amt;
      setRefundModal(null);
      alert(
        `✓ تم استرداد المبلغ.\n\n` +
        `• المدفوع فعلياً: ${fmtNum(paid)} ر.س\n` +
        `• الاسترداد الإجمالي: ${fmtNum(amt)} ر.س\n` +
        (mode === "exempt"
          ? `• الأتعاب الأولية: مُعفاة (0%)\n`
          : `• أتعاب المكتب (10%): ${fmtNum(ret)} ر.س\n`) +
        `• صافي للعميل: ${fmtNum(net)} ر.س\n\n` +
        `المعاملة منقولة إلى الأرشيف ومُغلَقة من البوابة.`
      );
      await onChange();
    } catch (e) { alert(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const refundAndArchive = openRefundModal;

  return (
    <div style={css.section}>
      {/* Termination banner — bright red so STAS can't miss it. */}
      {isTerminated && (
        <div style={{
          marginBottom: 14, padding: 12, borderRadius: 10,
          background: "rgba(220,38,38,0.10)", border: "2px solid #dc2626",
          fontWeight: 900, fontSize: 14, color: "#dc2626",
          textAlign: "center", letterSpacing: 1,
        }} data-testid="invoice-terminated-banner">
          {app.invoice_status === "refunded" ? "🚫 الفاتورة مسترجعة بالكامل — المعاملة في الأرشيف" : "❌ الفاتورة ملغية"}
        </div>
      )}

      {/* Read-only pricing breakdown — visible to ALL roles whenever items exist */}
      <PricingItemsView appId={app.id} hasItems={(app.pricing_items_count || 0) > 0} theme={theme} />

      {/* Invoice corrections — visible to STAS + GM + Deputy GM. */}
      {canEditInvoice && hasInvoice && !isTerminated && (
        <div style={{
          marginTop: 14, padding: 14, borderRadius: 10,
          background: "rgba(220,38,38,0.04)",
          border: "1.5px dashed rgba(220,38,38,0.5)",
        }} data-testid="stas-invoice-actions">
          <div style={{ fontSize: 11, fontWeight: 900, color: "#dc2626", marginBottom: 10, letterSpacing: 0.5 }}>
            إجراءات الفاتورة
            {!isManager && (
              <span style={{ fontWeight: 700, fontSize: 10, marginInlineStart: 8, opacity: 0.8 }}>
                — قبل الإرسال للعميل فقط
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={reviseInvoice} disabled={busy}
              style={{
                padding: "10px 16px", borderRadius: 8, border: "1.5px solid #0ea5e9",
                background: "rgba(14,165,233,0.10)", color: "#0ea5e9",
                fontWeight: 900, fontSize: 13, cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: 6, opacity: busy ? 0.5 : 1,
              }}
              data-testid="stas-revise-invoice">
              <Pencil size={14} strokeWidth={2.4} /> تعديل بنود الفاتورة
            </button>
            {/* Refund + archive is available for STAS + managers (was
                STAS-only). Employees never see this button. */}
            {canRefund && (
              <button onClick={refundAndArchive} disabled={busy}
                style={{
                  padding: "10px 16px", borderRadius: 8, border: "none",
                  background: "#dc2626", color: "#fff",
                  fontWeight: 900, fontSize: 13, cursor: "pointer",
                  display: "inline-flex", alignItems: "center", gap: 6, opacity: busy ? 0.5 : 1,
                }}
                data-testid="stas-refund-archive">
                <XOctagon size={14} strokeWidth={2.6} /> استرداد + أرشفة
              </button>
            )}
          </div>
          <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 8, lineHeight: 1.6 }}>
            <strong>تعديل البنود:</strong> تفتح نافذة كبيرة فيها كل البنود الحالية. تقدر تضيف بند جديد، تحذف، أو تغيّر السعر والكمية. النسخة الحالية تُختم «ملغية»، وتُصدر نسخة V2 بنفس رقم الفاتورة. كل التغييرات في الدفتر + الأرباح تنعكس تلقائياً.
            {isManager && (<><br /><strong>استرداد (مدراء):</strong> يقفل المعاملة كلياً ويرحّلها إلى الأرشيف. لا يمكن التراجع.</>)}
          </div>
        </div>
      )}

      {/* Invoice items editor modal — opens on "تعديل بنود الفاتورة". */}
      {editorOpen && (
        <InvoiceEditorModal
          app={app}
          theme={theme}
          onClose={() => setEditorOpen(false)}
          onSaved={async () => { setEditorOpen(false); await onChange(); }}
        />
      )}

      {/* Refund modal — explicit two-option flow (no confirm/cancel abuse). */}
      {refundModal && (
        <div
          data-testid="refund-modal"
          onClick={() => !busy && setRefundModal(null)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(8px)",
            zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: theme.surface, border: `1px solid ${theme.borderSoft}`,
              borderRadius: 16, width: "100%", maxWidth: 480, boxShadow: theme.sheetShadow || "0 20px 60px rgba(0,0,0,0.4)",
              fontFamily: "'Tajawal', sans-serif",
            }}
          >
            <div style={{ padding: "14px 20px", borderBottom: `1px solid ${theme.borderSoft}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <XOctagon size={20} color="#dc2626" />
                <div style={{ fontSize: 15, fontWeight: 900, color: theme.text }}>استرداد المبلغ</div>
              </div>
              <button type="button" onClick={() => setRefundModal(null)} disabled={busy}
                style={{ width: 30, height: 30, borderRadius: 8, background: "transparent", border: `1px solid ${theme.borderSoft}`, color: theme.text, cursor: "pointer" }}>×</button>
            </div>
            <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ fontSize: 12.5, color: theme.textSoft, lineHeight: 1.6, background: "rgba(212,175,55,0.08)", border: `1px solid ${theme.borderAccent}`, borderRadius: 10, padding: "10px 12px" }}>
                <strong>المدفوع فعلياً على هذه الفاتورة:</strong>{" "}
                <span dir="ltr" style={{ fontFamily: "SF Mono, ui-monospace, monospace" }}>{fmtNum(refundModal.paid)} ر.س</span>
                <div style={{ marginTop: 4, fontSize: 11, opacity: 0.85 }}>
                  يُحتسب الاسترداد من هذا المبلغ فقط، وليس من الإجمالي المطلوب.
                </div>
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 800, color: theme.textMuted, marginBottom: 6 }}>اختاري سياسة الاسترداد *</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <button type="button"
                    onClick={() => setRefundModal({ ...refundModal, mode: "charge" })}
                    data-testid="refund-mode-charge"
                    style={{
                      padding: "12px 10px", borderRadius: 12,
                      background: refundModal.mode === "charge" ? "rgba(245,158,11,0.14)" : "transparent",
                      border: `2px solid ${refundModal.mode === "charge" ? "#D97706" : theme.border}`,
                      color: theme.text, cursor: "pointer", fontFamily: "inherit", textAlign: "start",
                    }}>
                    <div style={{ fontWeight: 900, fontSize: 13, color: "#D97706", marginBottom: 3 }}>احتساب 10% للمكتب</div>
                    <div style={{ fontSize: 11, color: theme.textMuted, lineHeight: 1.5 }}>
                      يرد للعميل 90%<br />
                      <span dir="ltr">{fmtNum(Math.round(refundModal.paid * 0.9 * 100) / 100)} ر.س</span>
                    </div>
                  </button>
                  <button type="button"
                    onClick={() => setRefundModal({ ...refundModal, mode: "exempt" })}
                    data-testid="refund-mode-exempt"
                    style={{
                      padding: "12px 10px", borderRadius: 12,
                      background: refundModal.mode === "exempt" ? "rgba(22,163,74,0.14)" : "transparent",
                      border: `2px solid ${refundModal.mode === "exempt" ? "#16A34A" : theme.border}`,
                      color: theme.text, cursor: "pointer", fontFamily: "inherit", textAlign: "start",
                    }}>
                    <div style={{ fontWeight: 900, fontSize: 13, color: "#16A34A", marginBottom: 3 }}>إعفاء العميل</div>
                    <div style={{ fontSize: 11, color: theme.textMuted, lineHeight: 1.5 }}>
                      يرد للعميل 100%<br />
                      <span dir="ltr">{fmtNum(refundModal.paid)} ر.س</span>
                    </div>
                  </button>
                </div>
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 800, color: theme.textMuted, marginBottom: 6 }}>المبلغ المطلوب استرداده (ر.س) *</div>
                <input type="number" min="0" max={refundModal.paid} step="0.01"
                  value={refundModal.amount}
                  onChange={(e) => setRefundModal({ ...refundModal, amount: e.target.value })}
                  data-testid="refund-amount"
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 10, background: theme.inputBg, border: `1px solid ${theme.inputBorder}`, color: theme.text, fontSize: 14, fontFamily: "SF Mono, ui-monospace, monospace", direction: "ltr", boxSizing: "border-box" }} />
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 800, color: theme.textMuted, marginBottom: 6 }}>سبب الاسترداد (10 أحرف على الأقل) *</div>
                <textarea value={refundModal.reason}
                  onChange={(e) => setRefundModal({ ...refundModal, reason: e.target.value })}
                  data-testid="refund-reason"
                  rows={3}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 10, background: theme.inputBg, border: `1px solid ${theme.inputBorder}`, color: theme.text, fontSize: 13, fontFamily: "inherit", boxSizing: "border-box", resize: "vertical", lineHeight: 1.6 }} />
              </div>
            </div>
            <div style={{ padding: "12px 20px", borderTop: `1px solid ${theme.borderSoft}`, display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button type="button" onClick={() => setRefundModal(null)} disabled={busy}
                style={{ padding: "10px 16px", borderRadius: 999, background: "transparent", border: `1px solid ${theme.border}`, color: theme.text, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                إلغاء
              </button>
              <button type="button" onClick={confirmRefund} disabled={busy || !refundModal.mode}
                data-testid="refund-confirm"
                style={{ padding: "10px 18px", borderRadius: 999, background: "#dc2626", color: "#fff", border: "none", fontWeight: 900, fontSize: 13, cursor: "pointer", opacity: busy || !refundModal.mode ? 0.55 : 1, fontFamily: "inherit" }}>
                تنفيذ الاسترداد
              </button>
            </div>
          </div>
        </div>
      )}

      {!payments.length ? (
        <div style={{ marginTop: 12, opacity: 0.7 }}>لا توجد مدفوعات.</div>
      ) : payments.map((p) => {
        const color = ({ approved: "#16a34a", rejected: "#dc2626", pending: "#f59e0b" })[p.status] || theme.textMuted;
        return (
          <div key={p.id} style={css.paymentCard} data-testid={`payment-${p.id}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div>
                <div style={{ fontWeight: 900, fontSize: 18 }}><Money amount={p.amount} size={16} bold /></div>
                <div style={{ fontSize: 11, color: theme.textMuted }}>رُفع: {fmt(p.uploaded_at)}</div>
              </div>
              <span style={{ background: color + "22", color, padding: "5px 12px", borderRadius: 10, fontSize: 11, fontWeight: 800 }}>
                {{ approved: "معتمد", rejected: "مرفوض", pending: "بانتظار المراجعة" }[p.status]}
              </span>
            </div>

            {p.receipt && (
              <a href={url(p.receipt.file_url)} target="_blank" rel="noreferrer" style={css.receiptLink} data-testid={`payment-receipt-${p.id}`}>
                📎 {p.receipt.file_name}
              </a>
            )}

            {p.reviewer && (
              <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 8 }}>
                {p.status === "approved" ? "اعتمد" : "راجع"}: {p.reviewer.full_name} • {fmt(p.reviewed_at)}
              </div>
            )}
            {p.review_note && <div style={{ fontSize: 12, marginTop: 6, padding: 8, background: theme.surface, borderRadius: 6 }}>{p.review_note}</div>}

            {/* Open the official client receipt (same page the client sees,
                same one used for printing/PDF). Available only after approval. */}
            {p.status === "approved" && app.application_number && (
              <a
                href={`/portal/track/${app.application_number}/receipt`}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  marginTop: 12, padding: "8px 14px", borderRadius: 8,
                  background: theme.gold, color: "#0a0a0a", fontWeight: 900,
                  fontSize: 12, textDecoration: "none",
                }}
                data-testid={`open-receipt-${p.id}`}
              >
          {T.open_official_receipt}
              </a>
            )}

            {p.status === "pending" && canApprove && (
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button onClick={() => review(p.id, "approve")} disabled={busy} style={css.btnGreen} data-testid={`pay-approve-${p.id}`}>اعتماد</button>
                <button onClick={() => review(p.id, "reject")} disabled={busy} style={css.btnRed} data-testid={`pay-reject-${p.id}`}>رفض</button>
              </div>
            )}
            {p.status === "pending" && !canApprove && (
              <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 8 }}>
                * اعتماد/رفض السداد متاح فقط للمدير العام والنائب و STAS.
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─────────────────────── Timeline ─────────────────────── */
function Timeline({ items, css, theme, lang }) {
  const isAr = lang !== "en";
  if (!items.length) return <div style={css.section}>{isAr ? "لا توجد أحداث في السجل بعد." : "No events recorded yet."}</div>;
  const fmt = (d) => d ? fmtDateTime(d) : "—";
  return (
    <div style={css.section}>
      <div style={css.tlWrap}>
        {items.map((e, i) => {
          const fromLbl = e.from_status_code ? statusLabel(e.from_status_code, lang) : "";
          const toLbl = statusLabel(e.to_status_code, lang) || e.to_status_code;
          return (
            <div key={e.id || i} style={css.tlRow} data-testid={`timeline-${i}`}>
              <div style={css.tlDot} />
              <div style={{ flex: 1, paddingBottom: 14 }}>
                <div style={{ fontWeight: 800, fontSize: 13 }}>
                  {fromLbl ? `${fromLbl} → ` : ""}{toLbl}
                </div>
                <div style={{ fontSize: 11, color: theme.textMuted }}>
                  {fmt(e.created_at)} {e.actor && `• ${e.actor.full_name}`}
                </div>
                {e.note && <div style={css.tlNote}>{e.note}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────── Notes ─────────────────────── */
function Notes({ notes, appId, onChange, css, theme }) {
  const { lang } = useI18n();
  const T = ld(lang);
  const [body, setBody] = useState("");
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.post(`/workspace/applications/${appId}/notes`, { body, is_client_visible: visible });
      setBody(""); setVisible(false);
      await onChange();
    } catch (e) { alert(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const fmt = (d) => d ? fmtDateTime(d) : "—";

  return (
    <div style={css.section}>
      <div style={css.noteForm}>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder={T.notes_add_placeholder}
                  style={css.textarea} data-testid="note-input" />
        <label style={css.noteVisible}>
          <input type="checkbox" checked={visible} onChange={(e) => setVisible(e.target.checked)} data-testid="note-client-visible" />
          <span>{T.notes_client_visible}</span>
        </label>
        <button onClick={add} disabled={busy || !body.trim()} style={css.btnPrimary} data-testid="note-submit">
          {busy ? "…" : T.notes_add}
        </button>
      </div>

      <div style={{ marginTop: 16 }}>
        {notes.length === 0 ? (
          <div style={{ color: theme.textMuted, fontSize: 13, padding: 12, textAlign: "center" }}>{T.notes_empty}</div>
        ) : notes.map((n) => (
          <div key={n.id} style={css.noteRow} data-testid={`note-${n.id}`}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 800 }}>{n.author?.full_name || "—"}</span>
              <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 10,
                background: n.is_client_visible ? "rgba(34,197,94,0.15)" : "rgba(245,158,11,0.15)",
                color: n.is_client_visible ? "#16a34a" : "#f59e0b" }}>
                {n.is_client_visible ? T.notes_pill_client : T.notes_pill_internal}
              </span>
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.7 }}>{n.body}</div>
            <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 6 }}>{fmt(n.created_at)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────── Appointment ─────────────────────── */
function Appointment({ app, appId, onChange, css, theme }) {
  const { lang } = useI18n();
  const T = ld(lang);
  const ap = app.appointment || {};
  // Prefer the auto-filled top-level fields (populated when the CLIENT
  // picks one of the proposed appointment options). This lets the
  // appointment tab open already filled with date/time/location/city/status.
  // The legacy `app.appointment` sub-object is the manual fallback.
  const initial = app.appointment_date || ap.appointment_date || "";
  const [form, setForm] = useState({
    visa_center: app.appointment_location || ap.visa_center || "",
    city: app.appointment_city || ap.city || "",
    appt_date: app.appointment_date || (initial ? initial.slice(0, 10) : ""),
    appt_time: app.appointment_time || (initial && initial.length >= 16 ? initial.slice(11, 16) : ""),
    reference_number: ap.reference_number || "",
    status: app.appointment_status || ap.status || "booked",
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Visual feedback shown right next to the upload button. `null` while idle,
  // `{ type: "success" | "error", msg: string }` after the API call.
  const [uploadFeedback, setUploadFeedback] = useState(null);
  const fileRef = useRef(null);
  const att = app.appointment_attachment || null;
  const save = async () => {
    setBusy(true);
    try {
      // Re-compose to ISO-like "YYYY-MM-DDTHH:mm" — backend stores it as a string field.
      const composed = form.appt_date
        ? (form.appt_time ? `${form.appt_date}T${form.appt_time}` : form.appt_date)
        : "";
      const payload = {
        visa_center: form.visa_center,
        city: form.city,
        appointment_date: composed || null,
        reference_number: form.reference_number,
        status: form.status,
      };
      await api.post(`/workspace/applications/${appId}/appointment`, payload);
      await onChange();
    } catch (e) { alert(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const uploadAttachment = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadFeedback(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/workspace/applications/${appId}/appointment-attachment`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Friendly explicit confirmation — keeps user from wondering "did it
      // upload or not?". Includes file name + size so it's unambiguous.
      const kb = Math.round((file.size || 0) / 1024);
      setUploadFeedback({
        type: "success",
        msg: `تم رفع المرفق بنجاح: ${file.name} (${kb} كيلو) — العميل سيراه فوراً عند فتح صفحة المتابعة.`,
      });
      await onChange();
      // Auto-dismiss after 8s so the panel stays clean.
      setTimeout(() => setUploadFeedback((s) => (s && s.type === "success" ? null : s)), 8000);
    } catch (err) {
      setUploadFeedback({
        type: "error",
        msg: `فشل رفع المرفق: ${formatApiError(err)}`,
      });
    }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const removeAttachment = async () => {
    if (!window.confirm(T.appt_file_confirm_remove)) return;
    setBusy(true);
    try {
      await api.delete(`/workspace/applications/${appId}/appointment-attachment`);
      await onChange();
    } catch (e) { alert(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/portal/track/${app.application_number}/appointment-attachment`;
  return (
    <div style={css.section}>
      <Field label={T.appt_visa_center}>
        <input value={form.visa_center} onChange={(e) => setForm({ ...form, visa_center: e.target.value })} style={css.input} data-testid="appt-center" />
      </Field>
      <Field label={T.appt_city}>
        <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} style={css.input} data-testid="appt-city" />
      </Field>
      <Field label={T.appt_date}>
        <input
          type="date"
          value={form.appt_date}
          onChange={(e) => setForm({ ...form, appt_date: e.target.value })}
          style={css.input}
          data-testid="appt-date"
        />
      </Field>
      <Field label={T.appt_time}>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-2][0-9]:[0-5][0-9]"
          maxLength={5}
          placeholder={T.appt_time_ph}
          value={form.appt_time}
          onChange={(e) => {
            // Auto-insert colon after 2 digits, keep only digits + colon, max 5 chars.
            let v = e.target.value.replace(/[^\d:]/g, "");
            if (v.length === 2 && !v.includes(":") && form.appt_time.length < 2) v += ":";
            if (v.length > 5) v = v.slice(0, 5);
            setForm({ ...form, appt_time: v });
          }}
          style={{ ...css.input, textAlign: "center", letterSpacing: 2, fontVariantNumeric: "tabular-nums" }}
          dir="ltr"
          data-testid="appt-time"
        />
      </Field>
      <Field label={T.appt_ref}>
        <input value={form.reference_number} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} style={css.input} dir="ltr" data-testid="appt-ref" />
      </Field>
      <Field label={T.appt_status}>
        <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} style={css.input}>
          <option value="booked">{T.appt_status_booked}</option>
          <option value="rescheduled">{T.appt_status_rescheduled}</option>
          <option value="done">{T.appt_status_done}</option>
          <option value="cancelled">{T.appt_status_cancelled}</option>
        </select>
      </Field>
      <button onClick={save} disabled={busy} style={{ ...css.btnPrimary, width: "100%", marginTop: 8 }} data-testid="appt-save">
        {busy ? "…" : T.appt_save}
      </button>

      {/* Office-side upload: appointment PDF, invoice, or any file the client should download */}
      <div style={{
        marginTop: 18, padding: 14, borderRadius: 10,
        background: theme.surface, border: `1px dashed ${theme.borderSoft}`,
      }} data-testid="appt-attachment-section">
        <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 8, color: theme.gold }}>
          {T.appt_file_title}
        </div>
        <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 10, lineHeight: 1.7 }}>
          {T.appt_file_desc}
        </div>
        {/* Visual confirmation banner — succeeds/fails feedback right inside
            the upload section so the user never wonders "did it work?" */}
        {uploadFeedback && (
          <div
            data-testid="appt-upload-feedback"
            style={{
              padding: "10px 12px", borderRadius: 8,
              fontSize: 12.5, fontWeight: 700, lineHeight: 1.7, marginBottom: 10,
              display: "flex", alignItems: "flex-start", gap: 8,
              background: uploadFeedback.type === "success"
                ? "rgba(22,163,74,0.12)" : "rgba(220,38,38,0.10)",
              border: `1.5px solid ${uploadFeedback.type === "success" ? "#16a34a" : "#dc2626"}`,
              color: uploadFeedback.type === "success" ? "#16a34a" : "#dc2626",
            }}
          >
            {uploadFeedback.type === "success"
              ? <CheckCircle2 size={16} strokeWidth={2.5} style={{ flexShrink: 0, marginTop: 2 }} />
              : <AlertCircle size={16} strokeWidth={2.5} style={{ flexShrink: 0, marginTop: 2 }} />}
            <span>{uploadFeedback.msg}</span>
          </div>
        )}
        {att ? (
          <div style={{
            display: "flex", flexDirection: "column", gap: 8,
            padding: 10, background: theme.bg, borderRadius: 8,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 700 }}>
                <span dir="ltr">{att.filename}</span>
                <span style={{ color: theme.textMuted, fontSize: 11, marginInlineStart: 8 }}>
                  ({Math.round((att.size || 0) / 1024)} {T.appt_file_size})
                </span>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <a href={downloadUrl} target="_blank" rel="noopener noreferrer"
                  style={{ ...css.btnPrimary, padding: "6px 12px", fontSize: 11, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}
                  data-testid="appt-attachment-view">{T.appt_file_preview}</a>
                {/* NEW — direct replace: pick a new file and the backend overwrites
                    in-place (single-step refresh for appointment renewals). */}
                <input ref={fileRef} type="file" accept="application/pdf,image/png,image/jpeg,image/webp"
                  onChange={uploadAttachment} style={{ display: "none" }} data-testid="appt-attachment-replace-input" />
                <button onClick={() => fileRef.current?.click()} disabled={uploading || busy}
                  style={{ ...css.btnPrimary, padding: "6px 12px", fontSize: 11, background: theme.gold, color: "#0a0a0a" }}
                  data-testid="appt-attachment-replace">
                  {uploading ? (lang === "ar" ? "...جاري" : "...") : (lang === "ar" ? "استبدال" : "Replace")}
                </button>
                <button onClick={removeAttachment} disabled={busy}
                  style={{ ...css.btnDanger, padding: "6px 12px", fontSize: 11 }}
                  data-testid="appt-attachment-remove">{T.appt_file_remove}</button>
              </div>
            </div>
            <div style={{ fontSize: 11, color: "#16a34a", fontWeight: 700 }}>
              ✓ {T.appt_file_available}
            </div>
          </div>
        ) : (
          <>
            <input ref={fileRef} type="file" accept="application/pdf,image/png,image/jpeg,image/webp"
              onChange={uploadAttachment} style={{ display: "none" }} data-testid="appt-attachment-input" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading}
              style={{ ...css.btnPrimary, width: "100%", padding: "10px 14px" }}
              data-testid="appt-attachment-upload">
              {uploading ? T.appt_file_uploading : T.appt_file_upload}
            </button>
          </>
        )}
      </div>

      {/* WhatsApp message generator — copy ready text to send the client */}
      <ClientMessageBox app={app} theme={theme} />
    </div>
  );
}

function ClientMessageBox({ app, theme }) {
  const [copied, setCopied] = useState(false);
  const clientName = app.client_full_name || app.client_name || "العميل";
  const appNum = app.application_number || "";
  const ref = app.invoice_ref_number || `INV-${appNum}`;
  const portalUrl = `${window.location.origin}/portal/track/${appNum}/receipt`;
  const text =
    `عزيزي ${clientName}،\n\n` +
    `تم اعتماد فاتورة معاملتك.\n\n` +
    `📄 رقم المعاملة: ${appNum}\n` +
    `🔖 الرقم المرجعي للفاتورة: ${ref}\n\n` +
    `يمكنك استعراض إيصالك من الرابط التالي:\n${portalUrl}\n\n` +
    `مع تحيات وكالة التأشيرات للسفر والسياحة`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { /* fallback */ }
  };
  const waNum = (app.client_mobile_number || "").replace(/[^\d]/g, "");
  const waHref = waNum ? `https://wa.me/${waNum}?text=${encodeURIComponent(text)}` : null;
  return (
    <div style={{
      marginTop: 18, padding: 14, borderRadius: 10,
      background: theme.surface, border: `1px dashed ${theme.borderSoft}`,
    }} data-testid="client-message-box">
      <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 8, color: theme.gold }}>
        رسالة جاهزة للعميل
      </div>
      <pre style={{
        background: theme.bg, color: theme.text, padding: 12, borderRadius: 8,
        fontSize: 11, lineHeight: 1.8, whiteSpace: "pre-wrap",
        fontFamily: "'Tajawal','Segoe UI',sans-serif", margin: 0,
      }}>{text}</pre>
      <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
        <button onClick={copy}
          style={{ flex: 1, padding: "8px 14px", borderRadius: 8, border: "none",
            background: copied ? "#16a34a" : theme.gold, color: copied ? "#fff" : "#0a0a0a",
            fontWeight: 900, fontSize: 12, cursor: "pointer", minWidth: 140 }}
          data-testid="copy-client-message">
          {copied ? "✓ تم النسخ" : "نسخ الرسالة"}
        </button>
        {waHref && (
          <a href={waHref} target="_blank" rel="noopener noreferrer"
            style={{ flex: 1, padding: "8px 14px", borderRadius: 8, textAlign: "center",
              background: "#25D366", color: "#fff", fontWeight: 900, fontSize: 12,
              textDecoration: "none", minWidth: 140 }}
            data-testid="wa-send-client-message">
            إرسال عبر واتساب
          </a>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block", marginBottom: 12 }}>
      <div style={{ fontSize: 11, marginBottom: 6, opacity: 0.7, letterSpacing: "0.4px" }}>{label}</div>
      {children}
    </label>
  );
}


const makeStyles = (theme) => ({
  page: {
    background: theme.bg, color: theme.text,
    fontFamily: "'Tajawal','Segoe UI',sans-serif",
    padding: "20px 20px 32px", maxWidth: 1200, margin: "0 auto",
  },
  head: {
    display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap",
    marginBottom: 14,
    padding: "12px 14px",
    background: theme.surface,
    border: `1px solid ${theme.borderSoft}`,
    borderRadius: 14,
  },
  back: {
    background: "transparent", border: `1px solid ${theme.border}`, color: theme.text,
    padding: "8px 12px", borderRadius: 10, cursor: "pointer", fontSize: 12,
    minHeight: 36,
  },
  appNo: { fontFamily: "monospace", fontWeight: 900, color: theme.gold, fontSize: 14, direction: "ltr" },
  appClient: { fontSize: 15, color: theme.text, marginTop: 2, fontWeight: 800 },
  statusBar: {
    background: theme.surface, border: `1px solid ${theme.borderSoft}`,
    borderRadius: 14, padding: 14, marginBottom: 14,
    display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 10, alignItems: "center",
  },
  statusLabel: { fontSize: 10, color: theme.textMuted, letterSpacing: "0.5px" },
  statusVal: { fontWeight: 800, fontSize: 16, marginTop: 2, color: theme.text },
  transitionBtn: (color) => ({
    padding: "7px 12px", borderRadius: 999, fontSize: 11, fontWeight: 800,
    border: `1px solid ${color || theme.border}`,
    background: "transparent", color: color || theme.text, cursor: "pointer",
  }),
  tabs: {
    display: "flex", gap: 4, overflowX: "auto",
    background: theme.surfaceAlt,
    border: `1px solid ${theme.borderSoft}`,
    borderRadius: 12, padding: 4,
    marginBottom: 14,
  },
  tab: (active) => ({
    padding: "9px 14px",
    borderRadius: 8, border: "none",
    background: active ? theme.surface : "transparent",
    color: active ? theme.gold : theme.textMuted,
    fontWeight: 800, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
    fontFamily: "inherit",
    minHeight: 36,
  }),
  section: {
    background: theme.surface, border: `1px solid ${theme.borderSoft}`,
    borderRadius: 14, padding: 16,
  },
  h3: { margin: "0 0 12px", color: theme.text, fontWeight: 800, fontSize: 14 },
  row: { display: "flex", justifyContent: "space-between", gap: 10, padding: "10px 0", borderBottom: `1px solid ${theme.borderSoft}`, fontSize: 13 },
  rowLabel: { color: theme.textMuted, flexShrink: 0 },
  rowValue: { fontWeight: 700, textAlign: "end" },
  memberCard: {
    background: theme.surfaceAlt, border: `1px solid ${theme.borderSoft}`,
    borderRadius: 12, padding: 12, marginBottom: 8,
  },
  docCard: {
    display: "block", background: theme.surfaceAlt, border: `1px solid ${theme.borderSoft}`,
    borderRadius: 12, overflow: "hidden", textDecoration: "none", color: "inherit",
    transition: "border-color 150ms",
  },
  docThumb: { width: "100%", height: 140, objectFit: "contain", background: "#f5f5f5", padding: 4 },
  docPdf: { height: 100, display: "flex", alignItems: "center", justifyContent: "center", background: "#dc2626", color: "#fff", fontWeight: 900, letterSpacing: "1px" },
  docName: { fontWeight: 700, fontSize: 12 },
  paymentCard: {
    background: theme.surfaceAlt, border: `1px solid ${theme.borderSoft}`,
    borderRadius: 12, padding: 14, marginBottom: 10,
  },
  receiptLink: {
    display: "inline-block", padding: "6px 12px", background: theme.bg,
    color: theme.gold, textDecoration: "none", borderRadius: 8,
    fontSize: 12, border: `1px solid ${theme.borderSoft}`,
  },
  btnGreen: { flex: 1, background: "#16a34a", color: "#fff", border: "none", padding: "10px", borderRadius: 10, fontWeight: 800, cursor: "pointer", fontSize: 13 },
  btnRed: { flex: 1, background: "#dc2626", color: "#fff", border: "none", padding: "10px", borderRadius: 10, fontWeight: 800, cursor: "pointer", fontSize: 13 },
  tlWrap: { position: "relative", paddingInlineStart: 18 },
  tlRow: { display: "flex", gap: 14, position: "relative" },
  tlDot: { width: 10, height: 10, borderRadius: "50%", background: theme.gold, marginTop: 6, flexShrink: 0, boxShadow: `0 0 0 3px ${theme.bg}` },
  tlNote: { fontSize: 12, color: theme.textSoft, marginTop: 6, padding: 8, background: theme.surfaceAlt, borderRadius: 8 },
  noteForm: { display: "flex", flexDirection: "column", gap: 10, padding: 14, background: theme.surfaceAlt, borderRadius: 12, border: `1px solid ${theme.borderSoft}` },
  textarea: {
    width: "100%", padding: 10, borderRadius: 10,
    border: `1px solid ${theme.inputBorder}`, background: theme.inputBg,
    color: theme.text, fontSize: 13, fontFamily: "inherit", boxSizing: "border-box", resize: "vertical",
  },
  noteVisible: { display: "flex", gap: 8, alignItems: "center", fontSize: 12 },
  btnPrimary: { background: theme.goldGradient, color: "#0A0F19", border: "none", padding: "10px 18px", borderRadius: 10, fontWeight: 800, cursor: "pointer", fontSize: 13 },
  btnGhost: { background: "transparent", color: theme.text, border: `1px solid ${theme.border}`, padding: "8px 14px", borderRadius: 10, fontWeight: 700, cursor: "pointer", fontSize: 12 },
  noteRow: { padding: 12, background: theme.surfaceAlt, border: `1px solid ${theme.borderSoft}`, borderRadius: 12, marginBottom: 8 },
  input: { width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${theme.inputBorder}`, background: theme.inputBg, color: theme.text, fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" },
  loading: { padding: 60, textAlign: "center", color: theme.textMuted },
  errBox: { padding: 14, background: theme.errorBg, border: `1px solid ${theme.errorBorder}`, color: theme.errorText, borderRadius: 12, marginTop: 20 },
});


// ─── Office-side: request labelled docs from the client ─────────────
function DocRequestsPanel({ appId, app, onChanged, theme, lang }) {
  const isAr = lang !== "en";
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const reqs = app.document_requests || [];

  const add = async () => {
    const t = title.trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      await api.post(`/workspace/applications/${appId}/doc-requests`, { title: t });
      setTitle("");
      if (onChanged) await onChanged();
    } catch (e) {
      alert(e?.response?.data?.detail || (isAr ? "تعذّر الإضافة" : "Failed"));
    } finally { setBusy(false); }
  };
  const del = async (reqId) => {
    if (!window.confirm(isAr ? "حذف هذا الطلب؟" : "Delete?")) return;
    try { await api.delete(`/workspace/applications/${appId}/doc-requests/${reqId}`); if (onChanged) await onChanged(); }
    catch (e) { alert(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <section data-testid="doc-requests-panel" style={{
      background: theme.surface, border: `1px solid ${theme.borderSoft}`,
      borderRadius: 12, padding: 16, marginTop: 14,
    }}>
      <h3 style={{ fontSize: 14, fontWeight: 900, color: theme.gold, margin: "0 0 12px" }}>
        {isAr ? "📎 طلب مستندات من العميل" : "📎 Request documents from client"}
      </h3>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <input value={title} onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder={isAr ? "اسم المستند (مثل: كشف بنكي آخر 3 شهور)" : "Document title"}
          style={{ flex: 1, padding: "10px 12px", borderRadius: 8,
            border: `1px solid ${theme.inputBorder}`, background: theme.inputBg,
            color: theme.text, fontSize: 13, fontFamily: "inherit" }}
          data-testid="doc-request-title-input" />
        <button onClick={add} disabled={busy || !title.trim()}
          data-testid="doc-request-add-btn"
          style={{ padding: "10px 18px", borderRadius: 8, border: "none",
            background: theme.gold, color: "#1a1a1a", fontWeight: 800,
            fontSize: 13, cursor: busy ? "default" : "pointer",
            opacity: (busy || !title.trim()) ? 0.5 : 1, fontFamily: "inherit" }}>
          {isAr ? "+ إضافة" : "+ Add"}
        </button>
      </div>
      {reqs.length === 0 ? (
        <div style={{ fontSize: 12, color: theme.textMuted, padding: 12, textAlign: "center" }}>
          {isAr ? "لا توجد طلبات مستندات بعد" : "No document requests yet"}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {reqs.map((r, idx) => (
            <div key={r.id} data-testid={`doc-request-admin-row-${idx + 1}`} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "9px 12px",
              background: r.status === "uploaded" ? "rgba(22,163,74,0.08)" : theme.surfaceCard,
              border: `1px solid ${r.status === "uploaded" ? "rgba(22,163,74,0.35)" : theme.borderSoft}`,
              borderRadius: 8, fontSize: 13,
            }}>
              <div style={{
                width: 22, height: 22, borderRadius: "50%",
                background: r.status === "uploaded" ? "#16a34a" : theme.gold,
                color: r.status === "uploaded" ? "#fff" : "#1a1a1a",
                fontWeight: 900, fontSize: 11, display: "flex",
                alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>{idx + 1}</div>
              <div style={{ flex: 1, color: theme.text, fontWeight: 700 }}>{r.title}</div>
              <span style={{
                fontSize: 11, fontWeight: 800, padding: "3px 8px", borderRadius: 999,
                background: r.status === "uploaded" ? "rgba(22,163,74,0.18)" : "rgba(220,38,38,0.12)",
                color: r.status === "uploaded" ? "#16a34a" : "#dc2626",
              }}>{r.status === "uploaded" ? (isAr ? "✓ تم الرفع" : "✓ Uploaded") : (isAr ? "بانتظار العميل" : "Pending")}</span>
              {r.status === "uploaded" && r.file_id && (
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/attachments/${r.file_id}`}
                  target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: 11, color: theme.gold, textDecoration: "underline" }}>
                  {isAr ? "عرض" : "View"}
                </a>
              )}
              <button onClick={() => del(r.id)}
                data-testid={`doc-request-delete-${idx + 1}`}
                style={{ background: "transparent", border: "none", cursor: "pointer",
                  color: "#dc2626", fontSize: 16, padding: 2 }}>×</button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * InvoiceEditorModal
 * ──────────────────
 * Large centered modal that lets STAS / GM / Deputy GM:
 *   • see every existing line item
 *   • edit label / amount / qty / office-revenue flag inline
 *   • delete a line
 *   • add a brand-new line
 *   • write a reason (≥10 chars)
 * On save it calls POST /admin/invoices/:appNum/revise, which marks the
 * previous version as cancelled and issues V2 with the same ref number.
 */
function InvoiceEditorModal({ app, theme, onClose, onSaved }) {
  // Start from the current items. Each row gets a temporary `_uid` so
  // React can track it while the user edits before save.
  const initial = (app.pricing_items || []).map((it, i) => ({
    _uid: `row-${i}-${it.code || it.label || Math.random().toString(36).slice(2)}`,
    code: it.code || null,
    label: it.label || it.name_ar || "",
    name_ar: it.name_ar || it.label || "",
    amount: Number(it.amount || 0),
    qty: Number(it.qty || 1),
    is_office_revenue: !!it.is_office_revenue,
  }));
  const [rows, setRows] = useState(initial);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const updateRow = (uid, k, v) =>
    setRows((s) => s.map((r) => r._uid === uid ? { ...r, [k]: v } : r));
  const removeRow = (uid) => setRows((s) => s.filter((r) => r._uid !== uid));
  const addRow = () => setRows((s) => [...s, {
    _uid: `new-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    code: null, label: "", name_ar: "", amount: 0, qty: 1, is_office_revenue: true,
  }]);
  const total = rows.reduce((sum, r) => sum + (Number(r.amount) || 0) * (Number(r.qty) || 1), 0);
  const save = async () => {
    setErr("");
    if (reason.trim().length < 10) { setErr("سبب التعديل مطلوب — 10 أحرف على الأقل."); return; }
    if (rows.length === 0) { setErr("يجب أن تحتوي الفاتورة على بند واحد على الأقل."); return; }
    for (const r of rows) {
      if (!r.label?.trim()) { setErr("كل بند يحتاج وصفاً."); return; }
      if (Number(r.amount) < 0) { setErr("لا يمكن أن يكون السعر سالباً."); return; }
      if (Number(r.qty) < 1) { setErr("الكمية لا تقل عن 1."); return; }
    }
    setBusy(true);
    try {
      const items = rows.map((r) => ({
        code: r.code, label: r.label, name_ar: r.name_ar || r.label,
        amount: Number(r.amount), qty: Number(r.qty),
        is_office_revenue: !!r.is_office_revenue,
      }));
      await api.post(`/admin/invoices/${app.application_number}/revise`,
        { reason: reason.trim(), items });
      onSaved?.();
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setBusy(false); }
  };
  return (
    <div style={modal.backdrop} onClick={onClose} data-testid="invoice-editor-modal">
      <div style={modal.dialog} onClick={(e) => e.stopPropagation()}>
        <div style={modal.head}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Pencil size={18} strokeWidth={2.4} style={{ color: "#0ea5e9" }} />
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 900 }}>تعديل بنود الفاتورة</h3>
          </div>
          <button onClick={onClose} style={modal.close} data-testid="invoice-editor-close" aria-label="close">
            <X size={18} />
          </button>
        </div>
        <div style={modal.body}>
          <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 14, lineHeight: 1.7 }}>
            الفاتورة الحالية رقم <strong dir="ltr">{app.invoice_ref_number || app.application_number}</strong> ستُختم
            كـ«ملغية - تم التعديل»، وتُصدر نسخة <strong>V2</strong> بنفس الرقم تحتوي على هذه البنود الجديدة.
          </div>

          <div style={modal.tableHeader}>
            <div>الوصف</div>
            <div style={{ textAlign: "center" }}>السعر</div>
            <div style={{ textAlign: "center" }}>الكمية</div>
            <div style={{ textAlign: "center" }}>إيراد المكتب</div>
            <div></div>
          </div>

          {rows.map((r) => (
            <div key={r._uid} style={modal.row} data-testid={`invoice-row-${r._uid}`}>
              <input
                type="text" value={r.label}
                onChange={(e) => updateRow(r._uid, "label", e.target.value)}
                placeholder="مثال: ترجمة معتمدة"
                style={modal.input}
                data-testid={`invoice-row-label-${r._uid}`}
              />
              <input
                type="number" min="0" step="0.01" value={r.amount}
                onChange={(e) => updateRow(r._uid, "amount", e.target.value)}
                style={{ ...modal.input, textAlign: "center" }}
                data-testid={`invoice-row-amount-${r._uid}`}
              />
              <input
                type="number" min="1" step="1" value={r.qty}
                onChange={(e) => updateRow(r._uid, "qty", e.target.value)}
                style={{ ...modal.input, textAlign: "center" }}
                data-testid={`invoice-row-qty-${r._uid}`}
              />
              <div style={{ textAlign: "center" }}>
                <input
                  type="checkbox" checked={r.is_office_revenue}
                  onChange={(e) => updateRow(r._uid, "is_office_revenue", e.target.checked)}
                  style={{ width: 18, height: 18, cursor: "pointer" }}
                  data-testid={`invoice-row-revenue-${r._uid}`}
                />
              </div>
              <button onClick={() => removeRow(r._uid)} style={modal.rowDel} aria-label="حذف"
                data-testid={`invoice-row-delete-${r._uid}`}>
                <Trash2 size={14} strokeWidth={2.4} />
              </button>
            </div>
          ))}

          <button onClick={addRow} style={modal.addRow} data-testid="invoice-editor-add-row">
            <Plus size={14} strokeWidth={2.4} /> إضافة بند جديد
          </button>

          <div style={modal.totalBar}>
            <span>المجموع:</span>
            <span style={{ color: "#d4af37", fontFamily: "monospace" }} dir="ltr">
              {total.toFixed(2)} ر.س
            </span>
          </div>

          <div style={{ marginTop: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 800, display: "block", marginBottom: 6 }}>
              سبب التعديل <span style={{ color: "#dc2626" }}>*</span> (يُسجَّل في سجل التدقيق)
            </label>
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="مثال: إضافة رسوم خدمة استعجال — بناءً على طلب العميل"
              style={{ ...modal.input, width: "100%", resize: "vertical", fontFamily: "inherit" }}
              data-testid="invoice-editor-reason"
            />
            <div style={{ fontSize: 10, opacity: 0.6, marginTop: 4 }}>
              {reason.trim().length}/10 حرف على الأقل
            </div>
          </div>

          {err && (
            <div style={{
              marginTop: 10, padding: 10, borderRadius: 8,
              background: "rgba(220,38,38,0.10)", color: "#dc2626",
              fontSize: 12, fontWeight: 700,
            }}>{err}</div>
          )}
        </div>
        <div style={modal.foot}>
          <button onClick={onClose} style={modal.btnGhost} data-testid="invoice-editor-cancel">إلغاء</button>
          <button onClick={save} disabled={busy} style={modal.btnPrimary} data-testid="invoice-editor-save">
            {busy ? "جاري الحفظ..." : "حفظ النسخة V2"}
          </button>
        </div>
      </div>
    </div>
  );
}

const modal = {
  backdrop: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 },
  dialog: { background: "#fff", color: "#1a1a1a", borderRadius: 16, width: "100%", maxWidth: 760, maxHeight: "92vh", display: "flex", flexDirection: "column", boxShadow: "0 24px 60px rgba(0,0,0,0.5)", fontFamily: "'Tajawal','Segoe UI',sans-serif" },
  head: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid rgba(0,0,0,0.08)" },
  body: { padding: "18px 20px", overflowY: "auto", flex: 1 },
  foot: { display: "flex", justifyContent: "flex-end", gap: 10, padding: "14px 20px", borderTop: "1px solid rgba(0,0,0,0.08)" },
  close: { background: "transparent", border: "none", color: "#666", cursor: "pointer", padding: 4, borderRadius: 8 },
  tableHeader: { display: "grid", gridTemplateColumns: "2.5fr 1fr 0.7fr 1fr 36px", gap: 6, fontSize: 11, fontWeight: 900, opacity: 0.7, padding: "0 4px 6px", borderBottom: "1px solid rgba(0,0,0,0.08)" },
  row: { display: "grid", gridTemplateColumns: "2.5fr 1fr 0.7fr 1fr 36px", gap: 6, padding: "6px 0", alignItems: "center" },
  input: { padding: "8px 10px", borderRadius: 8, border: "1px solid rgba(0,0,0,0.15)", fontSize: 13, background: "#fff", color: "#1a1a1a", boxSizing: "border-box", width: "100%", minWidth: 0 },
  rowDel: { background: "rgba(220,38,38,0.10)", color: "#dc2626", border: "1px solid #dc2626", padding: 6, borderRadius: 8, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" },
  addRow: { background: "transparent", color: "#0ea5e9", border: "1px dashed #0ea5e9", padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontWeight: 800, fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6, marginTop: 8 },
  totalBar: { marginTop: 14, padding: "10px 14px", background: "rgba(0,0,0,0.04)", borderRadius: 10, display: "flex", justifyContent: "space-between", fontWeight: 900, fontSize: 14 },
  btnGhost: { background: "transparent", color: "#1a1a1a", border: "1px solid rgba(0,0,0,0.2)", padding: "10px 18px", borderRadius: 10, fontWeight: 800, fontSize: 13, cursor: "pointer" },
  btnPrimary: { background: "#0ea5e9", color: "#fff", border: "none", padding: "10px 22px", borderRadius: 10, fontWeight: 900, fontSize: 13, cursor: "pointer" },
};


/**
 * AppointmentAdminOverride
 * ────────────────────────
 * STAS / Manager-only panel that lets an admin change the appointment
 * date+time on ANY application — even legacy ones the client already
 * locked. The backend records old/new + reason in the audit log.
 */
function AppointmentAdminOverride({ application, onChanged }) {
  const { lang } = useI18n();
  const isAr = lang !== "en";
  const appId = application?.id || application?._id;
  // Pre-fill with the current appointment date+time (whatever shape it lives in).
  const initialIso = (() => {
    const d = application?.appointment_date || application?.appointment_chosen?.datetime?.slice(0, 10) || "";
    const t = application?.appointment_time || application?.appointment_chosen?.datetime?.slice(11, 16) || "";
    if (d && t) return `${d}T${t.length === 5 ? t : t.padStart(5, "0")}`;
    if (application?.appointment_chosen?.datetime) {
      try { return new Date(application.appointment_chosen.datetime).toISOString().slice(0, 16); } catch { return ""; }
    }
    return "";
  })();
  const [open, setOpen] = useState(false);
  const [newDt, setNewDt] = useState(initialIso);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const hasAny = !!(application?.appointment_chosen || application?.appointment_date || application?.appointment);
  if (!appId) return null;
  const save = async () => {
    setBusy(true); setErr("");
    try {
      if (!newDt) throw new Error("التاريخ والوقت مطلوبان");
      const iso = new Date(newDt).toISOString();
      await api.patch(`/workspace/applications/${appId}/appointment-override`, { datetime: iso, reason });
      setOpen(false); setReason("");
      onChanged?.();
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setBusy(false); }
  };
  return (
    <section style={{
      marginTop: 16, padding: 16, borderRadius: 12,
      background: "rgba(220,38,38,0.06)",
      border: "1.5px solid rgba(220,38,38,0.35)",
    }} data-testid="appointment-admin-override">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Pencil size={16} strokeWidth={2.4} style={{ color: "#dc2626" }} />
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 900, color: "#dc2626" }}>
            {isAr ? "تعديل الموعد (للمدراء فقط)" : "Override appointment (managers only)"}
          </h3>
        </div>
        {!open && (
          <button
            onClick={() => setOpen(true)}
            data-testid="appt-override-open"
            style={{
              background: "transparent", color: "#dc2626", border: "1px solid #dc2626",
              padding: "6px 14px", borderRadius: 8, fontWeight: 800, cursor: "pointer", fontSize: 12,
            }}
          >{isAr ? "تعديل" : "Edit"}</button>
        )}
      </div>
      {!open && (
        <p style={{ fontSize: 11, opacity: 0.75, marginTop: 8, marginBottom: 0, lineHeight: 1.7 }}>
          {hasAny
            ? (isAr
              ? "يمكنك تغيير تاريخ ووقت الموعد المثبَّت — حتى للمعاملات السابقة. كل تعديل يُسجَّل في السجل بالمستخدم والسبب."
              : "You can change the locked appointment date+time — even on legacy applications. Every change is audit-logged with user + reason.")
            : (isAr
              ? "لا يوجد موعد مسجَّل بعد. هذا الزر يستخدم لتعديل المواعيد القائمة فقط."
              : "No appointment recorded yet. This panel only edits existing appointments.")}
        </p>
      )}
      {open && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          <label style={{ fontSize: 12, fontWeight: 800 }}>
            {isAr ? "التاريخ والوقت الجديد:" : "New date and time:"}
          </label>
          <input
            type="datetime-local"
            value={newDt}
            onChange={(e) => setNewDt(e.target.value)}
            data-testid="appt-override-datetime"
            style={{ padding: "10px 12px", borderRadius: 8, border: "1px solid rgba(0,0,0,0.2)",
                     fontSize: 13, background: "rgba(255,255,255,0.04)", color: "inherit" }}
          />
          <label style={{ fontSize: 12, fontWeight: 800, marginTop: 6 }}>
            {isAr ? "سبب التعديل (يُسجَّل في الـ Audit):" : "Reason for override (audit-logged):"}
          </label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={isAr ? "مثال: السفارة غيّرت موعد البصمة" : "e.g. embassy rescheduled biometric"}
            data-testid="appt-override-reason"
            style={{ padding: "10px 12px", borderRadius: 8, border: "1px solid rgba(0,0,0,0.2)",
                     fontSize: 12, background: "rgba(255,255,255,0.04)", color: "inherit" }}
          />
          {err && <div style={{ color: "#dc2626", fontSize: 11, marginTop: 4 }}>{err}</div>}
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button onClick={save} disabled={busy}
              data-testid="appt-override-save"
              style={{ background: "#dc2626", color: "#fff", border: "none",
                       padding: "10px 18px", borderRadius: 10, fontWeight: 900,
                       fontSize: 13, cursor: "pointer", flex: 1 }}>
              {busy ? "..." : (isAr ? "حفظ التعديل" : "Save override")}
            </button>
            <button onClick={() => { setOpen(false); setErr(""); setReason(""); }}
              style={{ background: "transparent", color: "inherit", border: "1px solid rgba(0,0,0,0.2)",
                       padding: "10px 18px", borderRadius: 10, fontWeight: 800,
                       fontSize: 13, cursor: "pointer" }}>
              {isAr ? "إلغاء" : "Cancel"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

